import { interfaceEn } from './interface.js';
import { duesAttendanceEn } from './dues-attendance.js';

export const en = {
  ...interfaceEn,
  ...duesAttendanceEn,
  'app.name': 'Sports Team Management',
  'app.tagline': 'One secure place for your club operations',
  'locale.label': 'Language',
  'locale.en': 'English',
  'locale.hu': 'Hungarian',
  'locale.ro': 'Romanian',
  'nav.overview': 'Overview',
  'nav.athletes': 'Athletes',
  'nav.teams': 'Teams',
  'nav.calendar': 'Calendar',
  'nav.training': 'Training',
  'nav.assessments': 'Assessments',
  'nav.billing': 'Billing',
  'nav.payments': 'Payments',
  'nav.competitions': 'Competitions',
  'nav.reports': 'Reports',
  'nav.settings': 'Settings',
  'page.login': 'Sign in',
  'page.invite': 'Accept invitation',
  'page.notFound': 'Page not found',
  'help.login':
    'Choose Sign in to continue with your club account; you are returned here afterwards. A session ends after 30 minutes without activity for staff accounts and after 12 hours for family accounts, and after seven days at most. If sign-in fails, try again or contact your club secretary. Until your role is set up, no club data is shown.',
  'help.invite':
    'Open the invitation link your club sent you (for example by e-mail), or paste the invitation code here, and sign in to accept it. An invitation is valid for 72 hours, can be used once, and gives you only the roles and the people or accounts it names. Your email address alone never gives access to a child. Already have an account, for example from an earlier invitation or another club? Choose Sign in to accept. No account yet? Choose Create account. If the sign-in page says your e-mail address is already in use, you already have an account: sign in, using the forgotten-password link if needed. After signing in or registering you come back here with the invitation filled in; choose Accept invitation. If you were invited as a parent without a child, you see no children at first: open My children, use Add my child, and the child appears once the club approves it.',
  'help.notFound':
    'This page does not exist or you do not have access to it. Use the menu to return to an area you can open.',
  'help.overview':
    'The overview will summarize what needs your attention. It shows only the areas you are allowed to use. Club administrators and secretaries also see what needs attention: minors without a guardian link, guardian links and imports waiting for a decision, new joiners of the last 30 days and active athletes without a current club membership, each with a count and the first few names.',
  'help.athletes':
    'Athletes are listed alphabetically in a table; use the search box to find someone by name. Each row ends with action icons: the eye opens the athlete’s details (club membership periods, hockey profile and, for a guardian, change requests), and club administrators and secretaries also have the pencil to correct the record and the box to archive, withdraw or reactivate the athlete. People who manage billing also have the coin icon and a Record payment button in the details: it opens the billing account the athlete is on (choose one if there are several) and records a payment there, for example cash brought in by the athlete, with the same preview and allocation to the oldest open charges as on the billing page; an athlete who is on no billing account must first be assigned to one there. On a phone the date of birth, e-mail and phone columns are hidden; open the details to see them. Add an athlete, at the top of the page, opens the form for a new record. You only see the athletes your role covers: a guardian sees their own linked children, a coach sees the athletes of their department, and club administrators and secretaries see the whole club. Only club administrators and secretaries can add an athlete or correct a record, and only they see e-mail and phone. If an athlete with the same name and date of birth already exists you are warned and must confirm before a second record is created; records are never merged automatically. If someone else changed a record while you were editing it, reload and try again. Secretaries also see a Guardian links section: request a link between a guardian and an athlete (choose a guardian already in the club or enter a new one, the relationship and what the guardian may do), then approve, reject or revoke it. A link grants nothing until it is approved, you cannot approve a link that names you as the guardian, and revoking a link takes the child away from that guardian at once. Teams and training groups are managed on the Teams page. In an athlete’s details, managers and coaches see the Club membership periods; club administrators and secretaries can start a membership (the start date defaults to today) or end an open one, and ending keeps it as history. A membership cannot overlap another one of the same athlete, and it is independent of team and group placements: ending it does not remove the athlete from a team. When an athlete turns 18, the guardian’s access to that athlete pauses automatically; the Athletes turning 18 list shows who is coming up, lets you invite the athlete to their own account, and renews each link once the athlete has authorized it. You can also change what a guardian may do or the end date of an approved link. Club administrators and secretaries can also archive an athlete or record a withdrawal, with a reason: this ends the open club membership and the team and group placements as of today, and a withdrawal also ends the guardians’ access after today; everything stays as history, and reactivating an athlete restores none of it. Club administrators and secretaries also see an import section: download a CSV template (athletes, guardian links or memberships), fill it in UTF-8, choose the source system name and the date system the file uses, and check the file. Every row is checked and shown as ready, with errors, needing a decision, or already imported; a file with a formula, a spreadsheet or more than 1,000 rows is refused. A row that matches an existing athlete by name and birth date needs an explicit decision (create a new athlete, link it to the existing one, or skip it); records are never merged automatically. Nothing is saved until you import the ready rows, and only when no row has errors or waits for a decision. The import is applied all at once; if the club data changed in the meantime nothing is imported and you check the file again. Importing the same file again does not create duplicates, because rows are recognized by source and external ID. Guardian rows only create pending links with no access, and nobody is invited or notified. Each athlete also shows a Hockey profile with preferred positions (goalkeeper, defender, forward) and stick hand; everyone who can see the athlete reads it, club administrators and secretaries change it. The position played in a match is recorded on the match roster, not here. A guardian can ask for a change in the details of each linked child: new e-mail or phone, or a correction of the name or date of birth. Nothing changes until a secretary approves it; the secretary sees the current and proposed values side by side, must give a reason to reject, and cannot decide a request they made themselves. Name and birth date corrections should be checked against a document. A request that is still waiting can be withdrawn. A guardian also sees the club membership periods of a linked child when the link allows the membership view. Parents usually join by invitation: club administrators and secretaries use Invite a parent (e-mail address and name); the app e-mails the invitation when the club has a sender address, otherwise share the link shown; the parent signs in and uses Add my child to give each child’s name, date of birth and the relationship. A request gives no access by itself. Secretaries see it under Children requested by parents, together with the athletes born that day who share a name; they choose the right athlete or create a new record, keep or remove capabilities and approve, which opens an approved guardian link at once, or reject it with a reason the parent sees. A parent can withdraw a request that is still waiting. To tie a guardian to several existing athletes yourself, add each athlete in the guardian link form, and tick Approve the links now once you have verified the relationship. A link whose guardian has no account yet offers Invite to the app. Approving guardian links and child requests is a secretary task; a club administrator can add the Secretary role to their own account in Settings.',
  'help.teams':
    'Choose a season to see its teams and training groups, and open Show members to see who is on a roster. You only see the departments your role covers. Club administrators and secretaries can add a season (it starts as a draft; activate it when it begins and close it when it ends), add teams and training groups to a season that is not closed, and place athletes or end their placements. A placement needs a start date (default today); ending a placement keeps it as history, and a future end date keeps the athlete on the roster until that day. An athlete cannot be on the same team or in the same group twice for overlapping dates, and a shirt number can be used by one member of a team at a time. Age categories are labels only: they do not decide who is eligible for a competition. If someone else changed a season or placement while you were editing, reload and try again. To prepare the next season, use Plan the next season: choose which teams and training groups to keep (rename them or change their age category if you like), set the dates, and optionally place the athletes who are active today on the new teams. This only adds a new draft season; the old season, its teams and its rosters are never changed.',
  'help.calendar':
    'The calendar lists events by day for the period you choose. Staff see the events of the departments their role covers; families see only the published events of their own linked children, with the time, place and answer deadline. Club administrators, secretaries and coaches can add an event (a draft first), invite whole teams or training groups or open a roster with Pick players and tick only the players needed, from as many teams as they like (anyone else can be added by name), choose a rink, zone or room, and repeat it on chosen weekdays every week or every two, three or four weeks; a recurring event keeps its local start time through the change between summer and winter time. Publishing adds the invited athletes once, even if they belong to several teams or groups, and tells their families; cancelling keeps the event together with its reason, and a cancelled event that has not started can be restored with Restore event. Open any session of a series to see its next sessions: untick one to cancel only that session (for example tomorrow’s training) and tick it again to bring it back. Change schedule sets new weekdays, a new interval, time or end date from a chosen day on: sessions on days that stay keep their answers and attendance and move to the new time, sessions on dropped days are cancelled with the reason you give, new days are added, and earlier sessions do not change. When cancelling or restoring you can choose whether the invited families are notified; the notices are queued now and sent once notifications are switched on. Changing who is invited to a published event (Edit) updates its participants at once; anyone who already answered or has attendance recorded keeps their place. Two events cannot use the same resource at the same time (booking a whole rink blocks its zones, and cleanup time counts); a coach already booked elsewhere is refused, and athletes already expected at another event need your confirmation. Families answer yes, no or maybe until the deadline (two hours before the start unless the event says otherwise); a coach can correct an answer later with a reason. Open an event to see who answered and, for coaches, to record attendance with one tap per athlete: if another coach saved a row first, you see their entry and nothing is overwritten. If memberships change after publishing, check membership changes first; athletes who answered or have attendance recorded are never removed. Rinks, zones and rooms are set up under Venues and resources. The attendance rate is present plus late divided by present, late and both kinds of absence; cancelled events and rows not recorded are not counted.',
  'help.training':
    'Drills and training plans are prepared here by coaches, per department. A drill has a title in one or more languages, a duration, equipment and coaching instructions that only staff see; saving a change creates a new version and never alters plans that already use an older one. A plan is a list of blocks, each with an objective, a duration, an optional drill, intensity, equipment, surface and private coach notes; a block can have parallel stations and then lasts as long as its longest station, not the sum. The planned active time must fit the session after preparation and breaks, or you must give a reason. A plan starts as a draft; publishing freezes that version, and to change it you start a new version, so a session that already used it keeps exactly what it used. Choose which blocks get a short summary for families: drafts, objectives and coach notes are never shown to them. Attach a published plan version to an event on the Calendar page.',
  'help.assessments':
    'Coaches define assessment protocols here. A protocol sets the type of result (number, duration, yes or no, or a rubric of levels), the unit, how many decimals are kept, the lowest and highest valid value, whether lower or higher is better, how many attempts are allowed and how the result counts: the best attempt, the mean, the last valid attempt, or an attempt the coach chooses and gives a reason for. Saving a change creates a new version and never alters sessions that already used an older one; the distance, course, surface and method are kept with every result, and changing any of them starts a new series in the progress charts. To record results, create a session for a department, pick the protocols and the participants, then enter each attempt: leave an attempt empty if it was not made, enter zero only for a measured zero, and mark a spoiled attempt as invalid. Impossible values, such as a negative duration, too many decimals, a value outside the limits or another unit, are refused with a reason. Results stay drafts, visible only to the coaches of the department together with their private notes, until you publish them; publishing shows the counted result and the comment for families to the guardians whose link allows it and to the athlete. If another coach changed a row first you see their entry and nothing is overwritten. A published result is changed only through a correction with a reason: families keep seeing the published result until the correction is published, and the earlier version stays on record; an unpublished correction can be discarded, which leaves the published result as it was. Export results downloads the published results of a session as a CSV file in your language, without drafts, attempts or private notes; each download is recorded in the audit log. Group summaries are for staff only, are not shown for fewer than five participants, and there are no rankings. In Progress, choose an athlete to see the published results over time, one chart and table for each comparable protocol; a gap in the line means no valid value was recorded and is never read as zero. The change shown is a calculation about the measurements, not an assessment of a child’s worth or potential. When creating a session, you can add a whole team or training group of the department at once and then adjust the selection by name.',
  'help.billing':
    'Finance manages billing accounts here. The Billing accounts section opens with the page and can be folded; it lists one account per row with its balance, overdue amount, upcoming charges and unallocated credit (on a phone only the balance), and Open account shows it. On an account, the yearly membership fee and the monthly fee plans are separate sections: a club that only charges a yearly fee needs no monthly fee plan. A new fee plan starts in the club’s currency, set on the Settings page under Club data. Each account keeps its own balance; balances of different accounts are never added together. Post a charge with a due date, or post a payment: preview the oldest-due-first allocation, change the amounts if needed, then confirm. Whatever is not allocated stays as unallocated credit. Posted entries are never edited: to correct one, reverse it or post a credit note or refund with a reason. Retrying after a connection drop never posts twice. Fee plans set a monthly amount, a due day and the dates they apply to; a plan version is never edited, a change is saved as a new version. On an account, assign a fee plan to an athlete from a start date, optionally with an approved concession (a fixed amount or a percentage, always with a reason), and pause or end it when needed; an athlete has one fee at a time on an account, so a plan change is an end date plus a new assignment. For the monthly fee run, choose a month, preview what will be posted, then post it. The run posts one charge per assignment and month on the due day (a day beyond the end of the month falls on its last day); an assignment that starts mid-month waits for you to enter its first charge; paused or ended assignments are skipped; and a month that was already billed is never billed again, even if the run is posted twice. Under Automatic monthly run, finance can switch on automatic posting. It is off until you switch it on; once it is on, the system checks regularly and posts the current month’s charges for the club’s own calendar, exactly the rows the preview shows as “Will post”. Assignments that start mid-month still wait for your reviewed amount, a month already billed is never billed again, and the last automatic run (successful or failed) is shown under the switch. Switch it off at any time; charges already posted stay. On an account, the Statement shows every charge, credit note, payment, reversal and refund with a running balance. Choose a day to see the account as it stood at the end of that day, with overdue, upcoming charges and credit for that day; entries count from the moment they were recorded, so an earlier statement can always be reproduced. Download CSV saves the statement of the chosen day with the account and day at the top; each download is recorded in the audit trail. Under People with access, approve a person for this account with a permission and dates, or revoke an approval; approvals are per account and independent of any guardian link, and a revoked approval hides the account at once. For a payment with credit still available, Allocate credit proposes the oldest-due-first allocation of that credit for you to adjust and confirm. Under Reports, choose a day to see the whole club’s charges and payments as the ledger stood at the end of that day, one block per currency, never added together. Filter by season, by the team the athlete was on at the charge’s due date, by payment method or by account (a team filter leaves payments out and a method filter leaves charges out, because charges have no method and payments have no team). The reconciliation compares the charge, payment, allocation, refund and statement views of the same ledger and marks any difference. Download CSV saves the same figures with the day and filters at the top; each download is recorded in the audit trail. Under Closed periods, finance can lock the ledger through a past day: afterwards no charge due and no payment paid on or before that day can be posted, by hand or by the fee run (the run lists such assignments as skipped because the reporting period is closed). A close is final and always moves forward; to correct something in a closed period, post a credit note, reversal or refund, which is dated in the open period. Under Import, check a CSV file of billing accounts, athletes on accounts, opening balances or payments (download the template first; accounts and athletes are named by the external IDs of the same source, and athletes must be imported first). The check lists every problem per row and shows the totals of the valid rows to compare with your source sheet. Importing posts only new entries in one step: an opening balance becomes an opening charge or an opening credit, an imported payment stays unallocated credit, entries in a closed period are refused, and importing the same file again changes nothing. Under Yearly fees by team, choose a year and set the yearly fee of each age group (for example U7–U9 and U11 1000 RON, U13 and older 1300 RON); a change replaces the fee of that year and is recorded in the audit trail. The list below shows every account with its athletes and their team, the yearly fee, what was paid in that year and what is left to pay (or paid in excess). The team is the one the athlete is on at the start of the year, or the first one joined during it. All payments dated in the year count, net of refunds, even when they are not allocated to a charge; an account whose athlete has no team or no fee for its age group is marked incomplete. Filter by team or show only accounts with an amount left. This list posts nothing; to bill the fee as charges, use Yearly membership fee on the account.',
  'help.payments':
    'Payments lists the billing accounts the club has approved you to see. Each account has its own balance, overdue amount, upcoming charges and unallocated credit; balances of different accounts are never added together. Choose Show statement to see every charge, payment and refund of an account with a running balance, and pick a day to see the account as it stood at the end of that day. Being linked to a child does not show that child’s account: the club’s finance team approves each person for each account, and withdrawing the approval hides the account at once. This page only shows information; payments are recorded by the club. If a figure looks wrong, contact the club’s finance team.',
  'help.competitions':
    'Competition seasons tie a department’s season to one competition (federation, category, division and phases); competition managers (club administrators, secretaries and coaches) create them. Each competition lists its rule profile versions. A club administrator starts a draft from the shipped FRHG 2026–2027 catalog, records the SHA-256 of each source document that was read and the day the sources were checked for amendments, and then activates the version; activating supersedes the version that was active, and an activated version never changes. A draft, a superseded version or no profile at all never approves anyone: eligibility stays “needs review”. A draft that names no source cannot be activated, and the mandatory manual checks listed on a profile stay open. Under Registrations, a competition manager registers an athlete in the competition through a team of the same department and season, choosing the registration mode (native, temporary play-up or exclusive higher), the first day, an optional last day and an optional licence reference. An athlete can have only one active registration per competition on any day, but may be registered in several competitions at once. Correct changes the last day or the licence reference; End registration records the last day (not in the future) and a reason, and keeps the registration as history, so registering again creates a new one. A registration does not change club membership or any roster, and only people who may see the athlete see the registration. Under Documentary evidence, a competition manager records that a document (a medical clearance, an agreement, a loan or a transfer approval) exists for an athlete, who issued it and the days it is valid. A new record waits for a decision: Verify confirms that you saw the document, and Reject keeps the record as history with a reason. A record that is waiting can be corrected; a verified record can no longer be corrected, only withdrawn with a reason, and a replacement document is a new record. Only verified evidence counts towards eligibility; a record that is waiting or rejected never does. No copy of the document is stored, so do not write health details in notes or reasons. Under Suspensions, a competition manager records that an athlete may not play, either until a last day or for a number of games, in this competition or in every competition of the club, with an optional decision reference. While a suspension is active it can be corrected (the first day, the last day or the number of games, and the reference; a suspension counted in days stays in days and one counted in games stays in games), marked as served, or revoked with a reason. A served or revoked suspension stays as history and never changes. Only an active suspension counts towards eligibility. Under Matches, a competition manager attaches a match to the competition: choose a match event of the department that no other match uses yet (create it in the Calendar first), the club’s team, the phase, an opponent label and home or away. A match can be corrected (phase, opponent, home or away) while it is scheduled, marked completed once its day has come, or cancelled with a reason; a cancelled match frees its event. Completing a match records which rule profile version was active at that moment, and the match keeps it when a newer version is activated later. A completed or cancelled match stays as history and never changes. Under Roster, a competition manager selects the athletes for a scheduled match: search the athlete, choose the role in this match (goalkeeper, defender or forward) and an optional shirt number from 0 to 99 that no other athlete of the roster wears, then change or remove entries as needed. Each change is saved at once. A roster can change only while its match is scheduled; once the match is completed or cancelled its roster stays as it was. Selecting an athlete does not check eligibility, and a selected entry does not prove that the athlete played. Under Eligibility in the roster, a competition manager checks the roster shown against the competition’s active rule version: every athlete and the roster as a whole get Eligible, Not eligible or Needs review, with every reason and its source clause, and the result is saved with the day and the rule version it used. Without an active rule version nobody is eligible. Matches played outside the club count only once they are recorded under Matches elsewhere and a competition manager has attested that the history is complete; until then, wherever the rules limit matches per day or per week the best result is Needs review. The athlete’s other matches in the club count towards those limits too. When a registration, evidence, a suspension, the roster or the active rule version changes, the saved result is marked out of date and stays as history until you check again. The check covers only the automated rules; the manual checks listed under it stay with you. A registration also records the competition classification (general, girls or national-team female) that the rules use; it can be corrected while the registration is active and only competition managers see it. Finalize roster freezes the roster of a scheduled match: every athlete is checked again against the competition’s active rule version and the athlete’s other finalized rosters, and finalizing is refused when an athlete is not eligible or the roster breaks a rule. Where something still needs review (for example because matches played outside the club are not recorded), you must state in words that you reviewed it. A finalized roster cannot be changed or checked again until you reopen it with a reason; the saved check it was finalized on stays as history. When another roster is finalized, only finalized rosters count as appearances towards the daily and weekly limits, so if two rosters name the same athlete on the same day, the one finalized first stands and the second is refused. Export roster (CSV) downloads the roster shown as a file for internal use, with the shirt number, name, role and the outcome of a fresh check; it is always labelled an internal aid and is never a submission to the federation. The file says whether it is official-ready, which needs an active rule version, a finalized roster listed in full, no athlete or roster problem and every mandatory manual check recorded as done; otherwise it is a draft and the reasons are listed. Under Eligibility, each mandatory manual check of the active rule version can be recorded as done: write what you checked and the day you checked it, and use Withdraw record, with a reason, if it was recorded wrongly. A record counts only for the roster as it is now and for the rule version that is active now, so changing an entry or activating other rules opens the check again; recording works while the match is not cancelled, also after the roster is finalized. Under Matches elsewhere, a competition manager searches an athlete and records each verified match the athlete played outside the club’s rosters (the day, the competition or club, and where it was verified), then attests that every such match from one day through another (not in the future) is recorded. An attestation counts for a roster check only when it starts on or before the first day of the longest per-week window of the rules and ends on or after the day before the match, so renew it shortly before a match; a match played on the match day itself counts only if it is recorded. A record or an attestation is withdrawn with a reason; what was recorded never changes, a withdrawn one stays as history, and a correction is a withdrawal plus a new record. Do not write personal data in the source or the statement. Each export is written to the audit log.',
  'help.reports':
    'Reports and exports will be available here, limited to the data your role is allowed to see.',
  'help.settings':
    'Club administrators set the club’s data under Club data: the club name (shown in the page header and as the e-mail sender), the club language used for its e-mails, the time zone that decides calendar days and due dates, and the currency. The currency is used for new billing accounts, fee plans and yearly fees; existing accounts keep the currency they were opened in. Club administrators can add a staff role to another member or withdraw a role; a withdrawn role stops working at the member’s next action, and the club always keeps at least one administrator. A member can also be suspended: they are signed out of this club at once and cannot use any role until you reactivate them, and their roles are kept. To delete a member from the club, use Remove from club and confirm: every role they hold is withdrawn, they are signed out of this club and they disappear from the list, while attendance, payments and other history stay. A removed member can only come back by accepting a new invitation; their sign-in account and other clubs are not affected. Your language choice changes labels only. Club administrators can also invite a new staff member by e-mail: tick one or more roles (for example Club administrator, Coach and Secretary) and create the invitation; the person receives every ticked role when they accept. The app e-mails the invitation from the club’s sender address, in the club’s language; the link works for 72 hours, can be used once and can be withdrawn while waiting. To clean up the invitation list, tick finished invitations (accepted, withdrawn or expired) one by one or use Select all finished invitations, then Delete selected and confirm; waiting invitations cannot be deleted until you withdraw them, and the audit log still records every invitation. The link is also shown once with Copy link, so you can send it yourself if the e-mail could not be sent. Set the sender address under E-mail sender: invitations come from that address with the club name as sender, and the club’s mail server must be allowed to send for its domain. Without a sender address no e-mail is sent. Club administrators can also set how long staff and family sessions may stay idle and how many days a session may last; the new limits apply to sessions started after saving. Club administrators can also upload the club logo (PNG, JPEG or WebP); every member of the club sees it in the page header, and it can be replaced or removed at any time. Use Change end date beside a role to update its expiry or clear the date; saving signs that member out of this club. You cannot change the end date of your own role or a withdrawn role. An administrator can have an end date only while another active club administrator has no end date. A club-wide administrator can explicitly add Coach, Finance or Secretary to their own account, so one person can run a small club. Each addition is audited and requires signing in again; use Coach for attendance and Finance for fee payments. Members are listed by name and e-mail address as given at sign-in; a member who has not signed in since this was introduced shows the invitation address or is marked as not known yet. Parents are invited from the Athletes page with Invite a parent.',
  'sessionPolicy.title': 'Sign-in session limits',
  'sessionPolicy.note':
    'Staff are signed out after the idle minutes below and family accounts after the idle minutes for families; every session also ends after the maximum number of days. Changes apply to sessions started after you save.',
  'sessionPolicy.loading': 'Loading session limits…',
  'sessionPolicy.staffIdle': 'Staff idle limit (minutes)',
  'sessionPolicy.familyIdle': 'Family idle limit (minutes)',
  'sessionPolicy.absolute': 'Maximum session length (days)',
  'sessionPolicy.save': 'Save session limits',
  'sessionPolicy.saved': 'Session limits saved.',
  'mailSettings.title': 'E-mail sender',
  'mailSettings.note':
    'Invitation e-mails are sent from this address, with the club name as the sender name. Use an address of a domain your mail server may send for, otherwise recipients may not receive the e-mails. Leave it empty to stop sending e-mails.',
  'mailSettings.address': 'Sender e-mail address',
  'mailSettings.save': 'Save sender address',
  'mailSettings.saved': 'Sender address saved.',
  'mailSettings.loading': 'Loading e-mail settings…',
  'mailSettings.unavailable':
    'E-mail sending is not set up on this server yet, so no e-mail is sent even with an address. Ask the operator to configure the mail server.',
  'mailSettings.invalid': 'Enter a valid e-mail address, or leave the field empty.',
  'clubData.title': 'Club data',
  'clubData.note':
    'The club’s name appears in the page header and as the sender of its e-mails. The language is used for e-mails the club sends, the time zone decides the club’s calendar days (for example, when a monthly fee is due), and the currency is used for new billing accounts, fee plans and yearly fees.',
  'clubData.loading': 'Loading club data…',
  'clubData.saved': 'Club data saved.',
  'clubData.name': 'Club name',
  'clubData.nameRequired': 'Enter the club’s name.',
  'clubData.language': 'Club language',
  'clubData.languageHelp': 'Used for invitations and other e-mails from the club.',
  'clubData.timezone': 'Time zone',
  'clubData.timezoneHelp': 'Calendar days and due dates follow this time zone.',
  'clubData.currency': 'Currency',
  'clubData.currencyHelp':
    'Used for new accounts, fee plans and yearly fees. Existing accounts keep their currency.',
  'clubData.save': 'Save club data',
  'error.clubData.invalidTimeZone': 'This is not a known time zone.',
  'billing.accounts.table.account': 'Account',
  'billing.accounts.table.actions': 'Actions',
  'billing.fees.assign.noPlansInCurrency':
    'There is no monthly fee plan in this account’s currency. Create one under Fee plans & monthly fees on the Billing page.',
  'mail.invitation.subject': 'Invitation to {club}',
  'mail.invitation.greeting': 'Hello,',
  'mail.invitation.body': '{club} invites you to join it in {app} with these roles: {roles}.',
  'mail.invitation.action': 'Accept invitation',
  'mail.invitation.linkIntro': 'Link to the invitation:',
  'mail.invitation.expiry': 'The invitation can be used once and expires on {expiresAt}.',
  'mail.invitation.accountExisting':
    'You already have an account with this e-mail address, for example from an earlier invitation. Open the link and sign in with it; do not register again. If you have forgotten the password, use the forgotten-password link on the sign-in page.',
  'mail.invitation.accountNew':
    'No account yet? Open the link and choose Create account. If the sign-in page says the e-mail address is already in use, you already have an account: sign in instead, using the forgotten-password link if needed. Afterwards you return to the invitation to accept it.',
  'mail.invitation.ignore':
    'If you did not expect this invitation, ignore this e-mail; nothing changes without the link.',
  'logo.title': 'Club logo',
  'logo.note':
    'Shown in the page header to every member of the club. PNG, JPEG or WebP, up to {kilobytes} KB.',
  'logo.none': 'No logo yet.',
  'logo.current': 'Current club logo',
  'logo.choose': 'Choose logo',
  'logo.replace': 'Replace logo',
  'logo.remove': 'Remove logo',
  'logo.uploaded': 'Logo saved.',
  'logo.removed': 'Logo removed.',
  'logo.error.unsupportedType': 'Choose a PNG, JPEG or WebP image.',
  'logo.error.tooLarge': 'The image is too large. Choose a smaller one.',
  'roles.title': 'Roles and access',
  'roles.loading': 'Loading roles…',
  'roles.empty': 'No member holds a role you can manage yet.',
  'roles.you': '(you)',
  'roles.department': 'Department',
  'roles.scope.organization': 'Whole club',
  'roles.status.active': 'Active',
  'roles.status.revoked': 'Withdrawn',
  'roles.status.expired': 'Ended',
  'roles.until': 'until',
  'roles.revoke': 'Withdraw',
  'roles.revoke.confirm': 'Confirm withdrawal',
  'roles.assign.bundle': 'Add a staff role',
  'roles.assign.until': 'Ends on (optional)',
  'roles.assign.submit': 'Add role',
  'roles.assign.selfHelp':
    'As a club-wide administrator, you can add Coach, Finance or Secretary to your own account. Add Coach to record attendance and Finance to record fee payments. After each addition, sign in again to use the new role.',
  'roles.validity.edit': 'Change end date',
  'roles.validity.save': 'Save end date',
  'roles.validity.cancel': 'Cancel',
  'roles.validity.help':
    'Choose a future end date, or leave it blank for no end date. Dates use your device timezone. Saving signs this member out of this club. You cannot change the end date of your own role or a withdrawn role. An administrator can have an end date only while another active club administrator has no end date.',
  'roles.loadMore': 'Show more members',
  'roles.member.unnamed': "Name not known yet (shown after the member's next sign-in)",
  'roles.member.status.active': 'Active',
  'roles.member.status.suspended': 'Suspended',
  'roles.member.status.invited': 'Invited',
  'roles.member.status.revoked': 'Removed',
  'roles.member.suspend': 'Suspend member',
  'roles.member.suspend.confirm': 'Confirm suspension',
  'roles.member.reactivate': 'Reactivate member',
  'roles.member.remove': 'Remove from club',
  'roles.member.remove.confirm': 'Confirm removal',
  'roles.member.remove.warning':
    'Removing withdraws every role of this member, signs them out of this club and takes them off this list. Their history is kept. To bring them back, send them a new invitation.',
  'roles.bundle.club_admin': 'Club administrator',
  'roles.bundle.secretary': 'Secretary',
  'roles.bundle.finance': 'Finance',
  'roles.bundle.coach': 'Coach',
  'roles.bundle.registrar': 'Registrar (retired)',
  'roles.bundle.rules_officer': 'Rules officer (retired)',
  'roles.bundle.guardian': 'Guardian',
  'roles.bundle.athlete': 'Athlete',
  'invitations.title': 'Invitations',
  'invitations.loading': 'Loading invitations…',
  'invitations.empty': 'No invitations yet.',
  'invitations.create.email': 'E-mail address of the person to invite',
  'invitations.create.submit': 'Create invitation',
  'invitations.created':
    'Invitation created. The link below is shown only once and works for 72 hours; without an account the person can register on the sign-in page.',
  'invitations.link': 'Invitation link',
  'invitations.copy': 'Copy link',
  'invitations.copied': 'Link copied',
  'invitations.copyFailed': 'The link could not be copied; select it and copy it by hand.',
  'invitations.status.pending': 'Waiting',
  'invitations.status.accepted': 'Accepted',
  'invitations.status.revoked': 'Withdrawn',
  'invitations.status.expired': 'Expired',
  'invitations.expires': 'expires',
  'invitations.revoke': 'Withdraw invitation',
  'invitations.revoke.confirm': 'Confirm withdrawal',
  'invitations.loadMore': 'Show more invitations',
  'invitations.create.bundles': 'Roles to offer',
  'invitations.create.bundlesHint':
    'Tick every role the person should receive; all of them are granted when they accept.',
  'invitations.createdRoles': 'Roles in this invitation',
  'invitations.delivery.sent': 'The invitation was e-mailed to {email}.',
  'invitations.delivery.no_sender':
    'No e-mail was sent because the club has no sender address. Set one under E-mail sender, or send the link below yourself.',
  'invitations.delivery.unavailable':
    'E-mail sending is not set up on this server, so no e-mail was sent. Send the link below yourself.',
  'invitations.delivery.failed':
    'The invitation e-mail could not be sent. Send the link below yourself, or withdraw the invitation and try again later.',
  'invitations.emailed': 'E-mail sent',
  'invitations.select': 'Select the invitation to {email}',
  'invitations.selectAll': 'Select all finished invitations',
  'invitations.delete': 'Delete selected ({count})',
  'invitations.delete.confirm': 'Confirm deleting {count}',
  'invitations.delete.cancel': 'Cancel',
  'invitations.delete.hint': 'Waiting invitations cannot be deleted; withdraw them first.',
  'invitations.deleted': 'Invitations deleted: {count}.',
  'invitations.familyNote':
    'Parents are invited on the Athletes page under Invite a parent; athletes turning 18 are invited from the guardian access list.',
  'athletes.loading': 'Loading athletes…',
  'athletes.empty': 'No athletes to show.',
  'athletes.search.label': 'Search by name',
  'athletes.search.submit': 'Search',
  'athletes.dateOfBirth': 'Date of birth',
  'athletes.givenName': 'Given name',
  'athletes.familyName': 'Family name',
  'athletes.email': 'E-mail',
  'athletes.phone': 'Phone',
  'athletes.status.active': 'Active',
  'athletes.status.archived': 'Archived',
  'athletes.status.withdrawn': 'Withdrawn',
  'athletes.create.title': 'Add an athlete',
  'athletes.create.submit': 'Add athlete',
  'athletes.create.anyway': 'This is a different person — add anyway',
  'athletes.edit': 'Edit details',
  'athletes.edit.save': 'Save changes',
  'athletes.edit.cancel': 'Cancel',
  'athletes.loadMore': 'Show more athletes',
  'athletes.saved': 'Saved.',
  'athletes.table.name': 'Name',
  'athletes.table.status': 'Status',
  'athletes.table.actions': 'Actions',
  'athletes.action.details': 'Show details',
  'athletes.action.status': 'Change status',
  'athletes.action.payment': 'Record payment',
  'athletes.payment.loading': 'Loading billing accounts…',
  'athletes.payment.noAccount':
    'This athlete is not on a billing account yet. Assign them to an account on the Billing page first.',
  'athletes.payment.account': 'Billing account',
  'athletes.payment.recorded': 'Payment recorded.',
  'athletes.payment.openAccount': 'Open account',
  'athletes.payment.goToBilling': 'Go to Billing',
  'athletes.dialog.close': 'Close',
  'clubMembership.title': 'Club membership',
  'clubMembership.loading': 'Loading membership…',
  'clubMembership.empty': 'No club membership recorded.',
  'clubMembership.from': 'Member from',
  'clubMembership.until': 'until',
  'clubMembership.openEnded': 'no end date',
  'clubMembership.joinedOn': 'Member since (empty = today)',
  'clubMembership.join': 'Start membership',
  'clubMembership.end': 'End membership',
  'clubMembership.end.confirm': 'Confirm ending',
  'error.athlete.duplicateCandidate':
    'An athlete with this name and date of birth already exists. If this is a different person, confirm to add them anyway.',
  'error.athlete.dateOfBirthInFuture': 'The date of birth cannot be in the future.',
  'guardianLinks.title': 'Guardian links',
  'guardianLinks.intro':
    'A link gives a guardian access to one athlete only after you approve it. You cannot approve a link that names you as the guardian.',
  'guardianLinks.loading': 'Loading guardian links…',
  'guardianLinks.empty': 'No guardian links to show.',
  'guardianLinks.filter.label': 'Show',
  'guardianLinks.filter.all': 'All',
  'guardianLinks.status.pending': 'Waiting for approval',
  'guardianLinks.status.approved': 'Approved',
  'guardianLinks.status.rejected': 'Rejected',
  'guardianLinks.status.revoked': 'Revoked',
  'guardianLinks.athlete': 'Athlete',
  'guardianLinks.guardian': 'Guardian',
  'guardianLinks.relationship': 'Relationship',
  'guardianLinks.relationship.parent': 'Parent',
  'guardianLinks.relationship.legal_guardian': 'Legal guardian',
  'guardianLinks.relationship.grandparent': 'Grandparent',
  'guardianLinks.relationship.other': 'Other',
  'guardianLinks.validFrom': 'Valid from',
  'guardianLinks.validUntil': 'Valid until',
  'guardianLinks.openEnded': 'no end date',
  'guardianLinks.capabilities': 'What the guardian may do',
  'guardianLinks.capability.viewSchedule': 'See the schedule',
  'guardianLinks.capability.respondRsvp': 'Answer attendance requests',
  'guardianLinks.capability.viewPublishedResults': 'See published results',
  'guardianLinks.capability.viewMembership': 'See membership',
  'guardianLinks.capability.receiveNotices': 'Receive notices',
  'guardianLinks.approve': 'Approve',
  'guardianLinks.reject': 'Reject',
  'guardianLinks.revoke': 'Revoke',
  'guardianLinks.revoke.confirm': 'Confirm revoking',
  'guardianLinks.loadMore': 'Show more guardian links',
  'guardianLinks.saved': 'Saved.',
  'guardianLinks.create.title': 'Request a guardian link',
  'guardianLinks.create.submit': 'Request link',
  'guardianLinks.create.athleteSearch': 'Find the athlete by name',
  'guardianLinks.create.noMatches': 'No matches',
  'guardianLinks.create.existingGuardian': 'A guardian already in the club',
  'guardianLinks.create.newGuardian': 'A new guardian',
  'guardianLinks.create.guardianSearch': 'Find the guardian by name',
  'guardianLinks.create.guardianSource': 'Guardian',
  'childRequests.title': 'Add my child',
  'childRequests.hint':
    'Enter your child’s name and date of birth as the club knows them. A secretary checks the request and links the child to your account; until then you see nothing of the child.',
  'childRequests.note': 'Note for the secretary (optional)',
  'childRequests.submit': 'Send request',
  'childRequests.sent':
    'Request sent. The child appears under My children once a secretary approves it.',
  'childRequests.mine': 'My requests',
  'childRequests.loading': 'Loading requests…',
  'childRequests.empty': 'No requests yet.',
  'childRequests.withdraw': 'Withdraw',
  'childRequests.reason': 'Reason',
  'childRequests.status.pending': 'Waiting for review',
  'childRequests.status.approved': 'Approved',
  'childRequests.status.rejected': 'Rejected',
  'childRequests.status.withdrawn': 'Withdrawn',
  'childRequests.review.title': 'Children requested by parents',
  'childRequests.review.intro':
    'Check that the person is really the child’s parent or guardian before approving. Choose the athlete the request is about, or create a new athlete record; approving opens an approved guardian link at once, with the capabilities you leave ticked.',
  'childRequests.review.saved': 'Decision saved.',
  'childRequests.review.empty': 'No requests are waiting.',
  'childRequests.review.match': 'Which athlete is this?',
  'childRequests.review.exact': 'same name and date of birth',
  'childRequests.review.newAthlete': 'Create a new athlete record from this request',
  'childRequests.review.noCandidates': 'No athlete with this date of birth and name was found.',
  'childRequests.review.approve': 'Approve and link',
  'childRequests.review.reject': 'Reject',
  'childRequests.review.reject.confirm': 'Confirm rejection',
  'inviteParent.title': 'Invite a parent',
  'inviteParent.intro':
    'Enter the parent’s e-mail address and name. They sign in with the invitation link and then add their children; they see no child until a secretary approves the request. The app e-mails the invitation when the club has a sender address; the link is also shown once and works for 72 hours.',
  'inviteParent.submit': 'Create invitation',
  'guardianLinks.create.athletesHint':
    'Add one or more athletes; a separate link is requested for each.',
  'guardianLinks.create.chosenAthletes': 'Chosen athletes',
  'guardianLinks.create.chosenGuardian': 'Chosen guardian',
  'guardianLinks.create.approveNow': 'Approve the links now (I have verified the relationship)',
  'guardianLinks.invite.open': 'Invite to the app',
  'guardianLinks.invite.hint':
    'The guardian has no account yet. With the invitation they sign in and see this child while the link is approved.',
  'error.guardianLink.selfLink':
    'Nobody can be their own guardian or approve their own guardian access.',
  'error.guardianLink.alreadyOpen':
    'This guardian already has a waiting or approved link to this athlete for that period.',
  'error.guardianLink.invalidState':
    'This link has already been decided or changed. Reload the list and check its status.',
  'error.guardianLink.invalidValidity':
    'The validity period is not valid: it cannot end in the past or before it starts.',
  'shell.foundationTitle': 'The secure foundation is ready',
  'shell.foundationBody':
    'Feature areas will become available as their authorization and data protections are completed.',
  'shell.help':
    'Choose a language at any time. Your choice changes labels only; stored dates, numbers, and records remain unchanged.',
  'shell.placeholder': 'This area is not enabled yet.',
  'shell.skipToContent': 'Skip to main content',
  'login.signIn': 'Sign in with your club account',
  'login.error.loginFailed': 'Sign-in did not complete. Please try again.',
  'login.error.providerUnavailable':
    'The sign-in service is not available right now. Please try again in a few minutes.',
  'error.unauthenticated': 'Please sign in to continue.',
  'error.originNotAllowed':
    'The request was blocked because it did not come from this application.',
  'error.csrfInvalid': 'Your security token is missing or outdated. Reload the page and try again.',
  'page.landing': 'Welcome',
  'help.landing':
    'After you sign in you arrive at the overview of your club. If you belong to more than one club, choose one here or in the menu at the top; the data of each club is kept completely separate. If you opened an invitation before signing in, you are taken back to it to accept it. If your account is not linked to any club yet, choose Open the invitation page and paste the code from your invitation, or open its link again.',
  'account.loading': 'Loading your account…',
  'account.noMembership':
    'Your account is not linked to a club yet. Ask your club secretary for an invitation, then open its link or paste its code on the invitation page.',
  'account.openInvitation': 'Open the invitation page',
  'account.chooseOrganization': 'Choose a club',
  'account.organization': 'Club',
  'account.signOut': 'Sign out',
  'invite.tokenLabel': 'Invitation code',
  'invite.accept': 'Accept invitation',
  'invite.signInToAccept': 'Sign in to accept',
  'invite.createAccount': 'Create account',
  'invite.accountHint':
    'Already have an account, for example from an earlier invitation? Sign in. If the sign-in page says your e-mail address is already in use, you have an account: sign in, using the forgotten-password link if needed.',
  'error.unexpected': 'Something went wrong. Please try again.',
  'error.forbidden': 'You are not allowed to do that.',
  'error.mfaRequired':
    'Staff roles need multi-factor sign-in. Sign out, then sign in again and complete the second factor.',
  'error.import.empty': 'The file is empty.',
  'error.import.tooLarge': 'The file is too large; the limit is 512 KB of text.',
  'error.import.tooManyRows': 'The file has more than 1,000 rows. Split it into smaller files.',
  'error.import.tooManyColumns': 'The file has too many columns.',
  'error.import.cellTooLong': 'A cell is longer than 500 characters.',
  'error.import.formula':
    'The file contains a formula or a cell that starts with = or @. Remove it and try again.',
  'error.import.binary':
    'Only UTF-8 CSV text files are accepted, not spreadsheets or other binary files.',
  'error.import.unterminatedQuote': 'A quoted value is not closed.',
  'error.import.headerMismatch':
    'The first row must name exactly the columns of the template. Download the template and compare.',
  'error.import.duplicateHeader': 'A column name appears twice in the first row.',
  'error.import.notStaged': 'This import is already finished or withdrawn.',
  'error.import.unresolved':
    'Some rows still have errors or need a decision, so nothing was imported.',
  'error.import.stale':
    'The club data changed since this file was checked, so nothing was imported. Stage the file again.',
  'error.import.noDecisionNeeded': 'This row does not need a decision.',
  'error.import.invalidCandidate': 'Choose one of the existing athletes listed for this row.',
  'error.import.batchClosed': 'This import is closed; decisions can no longer be changed.',
  'error.import.unknownKind': 'Unknown import type.',
  'error.billing.unknownAthlete': 'This athlete is not in this club.',
  'error.billing.athleteNotOnAccount': 'This athlete is not assigned to this billing account.',
  'error.billing.assignmentOverlap':
    'This athlete already has a billing account for part of these dates.',
  'error.billing.assignmentAlreadyEnded': 'This assignment already has an end date.',
  'error.billing.invalidEnd': 'The end date cannot be before the start date.',
  'error.billing.accessAlreadyGranted':
    'This person already has access to this account. Revoke it first to change it.',
  'error.billing.accessAlreadyRevoked': 'This access was already revoked.',
  'error.billing.invalidValidity': 'The end of validity cannot be before its start.',
  'error.billing.currencyMismatch': 'The currency does not match the billing account.',
  'error.billing.accountClosed': 'This billing account is closed.',
  'error.billing.periodClosed':
    'This date lies in a closed reporting period. Post the entry in the open period instead.',
  'error.billing.periodClose.notPast': 'A period can only be closed through a day before today.',
  'error.billing.periodClose.notAfterLast':
    'The ledger is already closed through this day or a later one.',
  'error.billing.creditExceedsOutstanding':
    'A credit note cannot exceed what is still outstanding on the charge. Reverse an allocation first if the charge is already paid.',
  'error.billing.refundExceedsAvailable':
    "A refund cannot exceed the payment's unallocated value. Reverse an allocation first if the money is already allocated.",
  'error.billing.alreadyReversed': 'This entry was already reversed.',
  'error.billing.notReversible': 'This entry cannot be reversed.',
  'error.billing.paymentRefunded': 'A payment that was partly refunded cannot be reversed.',
  'error.billing.allocation.unknownCharge': "This charge does not belong to the payment's account.",
  'error.billing.allocation.duplicateCharge': 'A charge can appear only once in an allocation.',
  'error.billing.allocation.notPositive': 'An allocated amount must be greater than zero.',
  'error.billing.allocation.exceedsOutstanding':
    'The allocated amount exceeds what is outstanding on the charge.',
  'error.billing.allocation.exceedsPayment':
    'The allocations exceed the unallocated value of the payment.',
  'error.billing.fee.planCurrencyChanged':
    "A new version of a fee plan keeps the plan's currency. Start a new plan to use another currency.",
  'error.billing.fee.unknownAccount': 'This billing account does not exist.',
  'error.billing.fee.unknownPlan': 'This fee plan version does not exist.',
  'error.billing.fee.planCurrencyMismatch': "The fee plan's currency does not match the account.",
  'error.billing.fee.versionExists':
    'Another version of this plan was just created. Reload and try again.',
  'error.billing.fee.assignmentOverlap':
    'This athlete already has a fee on this account for part of these dates. End it first.',
  'error.billing.fee.periodTooFarAhead':
    'A fee run can cover the current month or the next month at most.',
  'error.billing.fee.tooManyAssignments':
    'Too many fee assignments touch this month to run at once.',
  'error.billing.fee.reviewNotApplicable':
    'Only a mid-month first charge that is still waiting for review can be given a reviewed amount.',
  'error.billing.fee.reviewExceedsPlan':
    'A reviewed first charge cannot be higher than the fee after the concession.',
  'error.idempotency.keyRequired': 'This action needs an Idempotency-Key header.',
  'error.idempotency.keyReused': 'This key was already used for a different request.',
  'error.idempotency.inProgress':
    'An earlier request with this key is still running. Try again shortly.',
  'import.problem.required': 'is required',
  'import.problem.too_long': 'is too long',
  'import.problem.invalid_date': 'is not a valid date in the chosen date system',
  'import.problem.date_in_future': 'is in the future',
  'import.problem.invalid_email': 'is not a valid e-mail address',
  'import.problem.invalid_phone': 'is not a valid phone number',
  'import.problem.invalid_locale': 'must be en, hu or ro',
  'import.problem.invalid_relationship': 'must be parent, legal_guardian, grandparent or other',
  'import.problem.end_before_start': 'is before the start date',
  'import.problem.duplicate_in_file': 'repeats a row earlier in this file',
  'import.problem.unknown_athlete': 'names an athlete that was not imported from this source yet',
  'import.problem.athlete_not_active': 'names an athlete who is not active',
  'import.problem.membership_overlap': 'overlaps an existing club membership of this athlete',
  'imports.title': 'Import athletes, guardians and memberships',
  'imports.intro':
    'Upload a UTF-8 CSV file made from the template. Nothing is saved until you review the checked rows and commit them.',
  'imports.kind': 'What to import',
  'imports.kind.athletes': 'Athletes',
  'imports.kind.guardians': 'Guardian links (pending)',
  'imports.kind.memberships': 'Club memberships',
  'imports.source': 'Source system name',
  'imports.sourceHelp': 'With an external ID it recognizes the same record when you import again.',
  'imports.dateFormat': 'Date system in the file',
  'imports.dateFormat.iso': 'Year-month-day (2026-10-04)',
  'imports.dateFormat.dmy': 'Day.month.year (04.10.2026)',
  'imports.dateFormat.mdy': 'Month/day/year (10/04/2026)',
  'imports.file': 'CSV file',
  'imports.fileTooLarge': 'This file is larger than 512 KB.',
  'imports.template': 'Download the template',
  'imports.stage': 'Check the file',
  'imports.staged': 'The file was checked. Review the result below before committing.',
  'imports.list': 'Imports',
  'imports.empty': 'No imports yet.',
  'imports.loading': 'Loading imports…',
  'imports.batchStatus.staged': 'Waiting for review',
  'imports.batchStatus.committed': 'Imported',
  'imports.batchStatus.cancelled': 'Withdrawn',
  'imports.rowStatus.ready': 'Ready',
  'imports.rowStatus.error': 'Errors',
  'imports.rowStatus.needs_decision': 'Need a decision',
  'imports.rowStatus.already_imported': 'Already imported',
  'imports.rowStatus.skipped': 'Skipped',
  'imports.rowStatus.committed': 'Imported now',
  'imports.review': 'Review',
  'imports.close': 'Close',
  'imports.commit': 'Import the ready rows',
  'imports.commit.blocked': 'Fix the file or decide every matched row first.',
  'imports.cancel': 'Withdraw this file',
  'imports.cancel.confirm': 'Confirm withdrawal',
  'imports.result': 'Import finished.',
  'imports.result.created': 'Created',
  'imports.result.linked': 'Linked to existing athletes',
  'imports.result.skipped': 'Skipped',
  'imports.result.alreadyImported': 'Already imported',
  'imports.row': 'Row',
  'imports.rows.empty': 'No rows in this group.',
  'imports.rows.loadMore': 'Show more rows',
  'imports.candidates': 'Existing athletes with the same name and birth date',
  'imports.decision.create': 'Create a new athlete',
  'imports.decision.link': 'Same person: link',
  'imports.decision.skip': 'Skip this row',
  'imports.decision.made': 'Decision',
  'imports.guardianNote':
    'Guardian rows create pending links with no access; a secretary approves each one under Guardian links. Nobody is invited or notified.',
  'imports.athleteFirstNote':
    'Import athletes first. Guardian and membership rows name an athlete by the external ID from the same source.',
  'import.problem.invalid_amount':
    'must be a whole number of minor units without separators (25000 means 250.00)',
  'import.problem.invalid_currency': 'must be RON, EUR or HUF',
  'import.problem.currency_mismatch': 'is not the currency of this account',
  'import.problem.invalid_balance_type': 'must be due or credit',
  'import.problem.invalid_method': 'must be cash, bank_transfer, card_terminal or other',
  'import.problem.unknown_account': 'names an account that was not imported from this source yet',
  'import.problem.account_closed': 'names an account that is closed',
  'import.problem.period_closed': 'falls in a reporting period that is already closed',
  'import.problem.assignment_overlap':
    'overlaps another billing account assignment of this athlete',
  'import.problem.athlete_not_on_account': 'names an athlete who is not assigned to this account',
  'imports.kind.billing_accounts': 'Billing accounts',
  'imports.kind.billing_assignments': 'Athletes on billing accounts',
  'imports.kind.opening_balances': 'Opening balances',
  'imports.kind.payments': 'Payments',
  'billing.imports.title': 'Import accounts, opening balances and payments',
  'billing.imports.intro':
    'Check a CSV file first; nothing is posted until you import the ready rows. A file only adds entries: posted charges and payments are never changed or overwritten, and nobody is notified.',
  'billing.imports.orderNote':
    'Import in this order: accounts, then athletes on accounts, then opening balances, then payments. Athletes must already be imported from the same source; rows name accounts and athletes by their external IDs.',
  'billing.imports.openingNote':
    'An opening balance becomes an opening charge (type due, dated by the as_of day) or an opening credit (type credit); it is never recorded as a historical payment. Amounts are whole minor units: 25000 means 250.00.',
  'billing.imports.paymentsNote':
    'An imported payment keeps its whole amount as unallocated credit; allocate it afterwards from the account.',
  'imports.totals': 'Totals of the valid rows — compare them with your source sheet',
  'imports.totals.opening_due': 'Opening dues',
  'imports.totals.opening_credit': 'Opening credits',
  'imports.totals.payment': 'Payments',
  'imports.totals.rows': 'rows',
  'dashboard.secretary.title': 'What needs your attention',
  'dashboard.loading': 'Loading…',
  'dashboard.missingGuardianLinks': 'Minors without a guardian link',
  'dashboard.pendingGuardianLinks': 'Guardian links waiting for a decision',
  'dashboard.pendingImports': 'Imports waiting for review',
  'dashboard.newJoiners': 'New joiners in the last 30 days',
  'dashboard.inactiveAthletes': 'Active athletes without a current club membership',
  'dashboard.evidenceNote':
    'Expiring registrations and evidence will appear here once competition registrations exist.',
  'dashboard.openAthletes': 'Open the Athletes page',
  'hockeyProfile.title': 'Hockey profile',
  'hockeyProfile.positions': 'Preferred positions',
  'hockeyProfile.position.goalkeeper': 'Goalkeeper',
  'hockeyProfile.position.defender': 'Defender',
  'hockeyProfile.position.forward': 'Forward',
  'hockeyProfile.stickHand': 'Stick hand',
  'hockeyProfile.stickHand.left': 'Left',
  'hockeyProfile.stickHand.right': 'Right',
  'hockeyProfile.none': 'Not set',
  'hockeyAssessment.skatingSprint': 'Straight-line skating sprint',
  'hockeyAssessment.puckControlCourse': 'Puck-control course',
  'hockeyAssessment.shootingAccuracy': 'Shooting accuracy',
  'hockeyAssessment.offIceAgility': 'Off-ice agility course',
  'hockeyProfile.save': 'Save hockey profile',
  'hockeyProfile.saved': 'Hockey profile saved.',
  'error.changeRequest.notPending': 'This request was already decided or withdrawn.',
  'error.changeRequest.ownRequest': 'You cannot decide a request you made yourself.',
  'error.changeRequest.athleteChanged':
    'The athlete record changed in the meantime. Reload and review again.',
  'error.childRequest.notPending': 'This request was already decided or withdrawn.',
  'error.childRequest.ownRequest':
    'You cannot decide a request you made or one that names you as the guardian.',
  'error.childRequest.noGuardianRecord':
    'Your account is not linked to a guardian record in this club yet. Ask the club to invite you as a parent.',
  'error.childRequest.unknownAthlete': 'The chosen athlete does not exist in this club.',
  'error.childRequest.alreadyPending': 'You already have a waiting request for this child.',
  'changeRequests.title': 'Change requests',
  'changeRequests.open': 'Request a change',
  'changeRequests.hint':
    'Fill in only what should change. A secretary reviews the request; nothing changes until it is approved.',
  'changeRequests.note': 'Note for the secretary (optional)',
  'changeRequests.submit': 'Send request',
  'changeRequests.cancel': 'Cancel',
  'changeRequests.sent': 'The request was sent for review.',
  'changeRequests.withdraw': 'Withdraw',
  'changeRequests.status.pending': 'Waiting for review',
  'changeRequests.status.approved': 'Approved',
  'changeRequests.status.rejected': 'Rejected',
  'changeRequests.status.withdrawn': 'Withdrawn',
  'changeRequests.review.title': 'Change requests to review',
  'changeRequests.sensitive': 'name or birth date: check a document',
  'changeRequests.approve': 'Approve and apply',
  'changeRequests.reject': 'Reject',
  'changeRequests.reject.confirm': 'Confirm rejection',
  'changeRequests.reason': 'Reason',
  'error.notFound': 'This item does not exist or you do not have access to it.',
  'error.validation': 'Some of the information is not valid. Check it and try again.',
  'error.logo.invalidImage':
    'The image is not valid or is too large. Choose a PNG, JPEG or WebP image within the size limit.',
  'error.versionConflict': 'Someone else changed this item. Reload it and try again.',
  'error.preconditionRequired':
    'The request does not say which version it changes. Reload the page and try again.',
  'error.invitation.invalid':
    'This invitation code is not valid. Check that you copied it completely.',
  'error.invitation.expired': 'This invitation has expired. Ask your club secretary for a new one.',
  'error.invitation.revoked':
    'This invitation was withdrawn. Ask your club secretary for a new one.',
  'error.invitation.alreadyUsed': 'This invitation has already been used or is no longer pending.',
  'error.invitation.ownInvitation': 'You cannot accept an invitation that you created yourself.',
  'error.invitation.membershipBlocked':
    'Your membership of this club cannot accept this invitation. Contact your club secretary.',
  'error.invitation.roleNotAllowed': 'You are not allowed to invite people to this role.',
  'error.invitation.personRequired':
    'Guardian and athlete invitations need a verified person link; staff invitations must not have one.',
  'error.invitation.stillPending':
    'A waiting invitation cannot be deleted; withdraw it first. Reload the list and try again.',
  'error.invitation.unknownDepartment': 'The selected department does not exist in this club.',
  'error.role.notAssignable':
    'This role cannot be assigned here. Guardian and athlete roles come only from an invitation with a verified person link.',
  'error.role.memberInactive': 'This membership is not active, so it cannot receive a role.',
  'error.role.unknownDepartment': 'The selected department does not exist in this club.',
  'error.role.alreadyAssigned': 'This person already holds this role in the selected scope.',
  'error.role.alreadyRevoked': 'This role was already withdrawn.',
  'error.role.invalidValidity': 'The end date must be in the future.',
  'error.role.lastAdministrator':
    'The club needs at least one active club administrator. Assign another administrator first.',
  'error.member.notChangeable':
    'This membership is no longer in a state that allows this change. Reload the list and try again.',
  'error.member.lastAdministrator':
    'The club needs at least one active club administrator. Assign another administrator first.',
  'teams.department': 'Department',
  'teams.noDepartments': 'There is no department to show.',
  'teams.loading': 'Loading…',
  'teams.saved': 'Saved.',
  'teams.season.select': 'Season',
  'teams.season.none': 'No seasons yet.',
  'teams.season.name': 'Season name',
  'teams.season.startsOn': 'Starts on',
  'teams.season.endsOn': 'Ends on',
  'teams.season.status.draft': 'Draft',
  'teams.season.status.active': 'Active',
  'teams.season.status.closed': 'Closed',
  'teams.season.create.title': 'Add a season',
  'teams.season.create.submit': 'Add season',
  'teams.season.activate': 'Activate season',
  'teams.season.close': 'Close season',
  'teams.season.close.confirm': 'Confirm closing',
  'teams.teams.title': 'Teams',
  'teams.teams.empty': 'No teams in this season yet.',
  'teams.team.name': 'Team name',
  'teams.team.category': 'Age category',
  'teams.team.division': 'Competition division (optional)',
  'teams.team.ordinal': 'Team number',
  'teams.team.create.title': 'Add a team',
  'teams.team.create.submit': 'Add team',
  'teams.category.U7_U9': 'U7–U9',
  'teams.category.U11': 'U11',
  'teams.category.U13': 'U13',
  'teams.category.U15': 'U15',
  'teams.category.U17': 'U17',
  'teams.category.U20': 'U20',
  'teams.groups.title': 'Training groups',
  'teams.groups.empty': 'No training groups in this season yet.',
  'teams.group.name': 'Group name',
  'teams.group.create.title': 'Add a training group',
  'teams.group.create.submit': 'Add group',
  'teams.roster.show': 'Show members',
  'teams.roster.hide': 'Hide members',
  'teams.roster.empty': 'No members.',
  'teams.roster.includeEnded': 'Include ended placements',
  'teams.roster.from': 'From',
  'teams.roster.until': 'Until',
  'teams.roster.shirt': 'Shirt number',
  'teams.roster.add.title': 'Add a member',
  'teams.roster.add.athlete': 'Search an athlete by name',
  'teams.roster.add.noMatches': 'No match',
  'teams.roster.add.submit': 'Add to roster',
  'teams.roster.end': 'End placement',
  'teams.roster.end.confirm': 'Confirm ending',
  'teams.roster.openEnded': 'no end date',
  'error.structure.unknownCategory':
    "This age category is not available for the department's sport.",
  'error.structure.invalidPeriod': 'The end date cannot be before the start date.',
  'error.structure.invalidState':
    'This season is no longer in a state that allows this change. Reload the page and try again.',
  'error.structure.notOpen':
    'The season or team is closed or archived, so it can no longer be changed.',
  'error.structure.alreadyExists':
    'An item with this name already exists in this department and season.',
  'error.structure.membershipOverlap':
    'This athlete already has a placement here that overlaps these dates.',
  'error.structure.shirtNumberTaken':
    'This shirt number is already used by another member on these dates.',
  'error.structure.athleteNotActive': 'Only active athletes can be placed on a team or in a group.',
  'error.structure.alreadyEnded': 'This placement already has an end date.',
  'error.structure.invalidEnd': 'The end date cannot be before the placement started.',
  'error.clubMembership.overlap':
    'This athlete already has a club membership that overlaps these dates.',
  'error.clubMembership.athleteNotActive': 'Only active athletes can start a club membership.',
  'error.clubMembership.alreadyEnded': 'This membership already has an end date.',
  'error.clubMembership.invalidEnd': 'The end date cannot be before the membership started.',
  'calendar.loading': 'Loading events…',
  'calendar.empty': 'No events in this period.',
  'calendar.saved': 'Saved.',
  'calendar.loadMore': 'Show more events',
  'calendar.staff.title': 'Club calendar',
  'calendar.family.title': 'Upcoming events',
  'calendar.family.empty': 'No upcoming events.',
  'calendar.filter.from': 'From',
  'calendar.filter.to': 'To',
  'calendar.filter.department': 'Department',
  'calendar.filter.status': 'Status',
  'calendar.filter.all': 'All',
  'calendar.type.on_ice_training': 'On-ice training',
  'calendar.type.off_ice_training': 'Off-ice training',
  'calendar.type.match': 'Match',
  'calendar.type.assessment': 'Assessment',
  'calendar.type.meeting': 'Meeting',
  'calendar.status.draft': 'Draft',
  'calendar.status.published': 'Published',
  'calendar.status.cancelled': 'Cancelled',
  'calendar.status.completed': 'Completed',
  'calendar.field.type': 'Type',
  'calendar.field.title': 'Title',
  'calendar.field.date': 'Date',
  'calendar.field.startTime': 'Starts',
  'calendar.field.endTime': 'Ends',
  'calendar.field.location': 'Location (optional)',
  'calendar.field.resource': 'Rink, zone or room',
  'calendar.field.noResource': 'No resource needed',
  'calendar.field.capacity': 'Capacity (optional)',
  'calendar.field.arrival': 'Arrive this many minutes before the start (optional)',
  'calendar.field.rsvpHours': 'Families can answer until this many hours before the start',
  'calendar.create.title': 'Add an event',
  'calendar.create.submit': 'Add event',
  'calendar.create.submitSeries': 'Add recurring series',
  'calendar.create.repeat': 'Repeat on a schedule',
  'calendar.create.weekdays': 'Repeat on',
  'calendar.create.until': 'Repeat until (optional)',
  'calendar.create.publishAll': 'Publish every occurrence now',
  'calendar.create.leadSelf': 'I lead this session',
  'calendar.create.created': 'Event added as a draft. Open it to publish.',
  'calendar.create.seriesResult': 'Events created:',
  'calendar.create.seriesConflicts': 'Left as drafts because the resource was already taken:',
  'calendar.create.seriesUnpublished': 'Left as drafts because they could not be published:',
  'calendar.audience.title': 'Who is invited',
  'calendar.audience.season': 'Season',
  'calendar.audience.teams': 'Teams',
  'calendar.audience.groups': 'Training groups',
  'calendar.audience.none': 'No teams or groups in this season.',
  'calendar.audience.athlete': 'Add an athlete by name',
  'calendar.audience.noMatches': 'No match',
  'calendar.audience.remove': 'Remove',
  'calendar.audience.hint':
    'Tick whole teams or groups, or open a roster and tick only the players you need, from as many teams as you like.',
  'calendar.audience.pickFromRoster': 'Pick players',
  'calendar.audience.hideRoster': 'Hide players',
  'calendar.audience.rosterEmpty': 'Nobody is on this roster today.',
  'calendar.audience.picked': 'Invited one by one',
  'calendar.audience.publishedHint':
    'Saving updates the participants at once: newly invited people are added, and anyone who already answered or has attendance recorded keeps their place.',
  'calendar.action.restore': 'Restore event',
  'calendar.series.title': 'Recurring series',
  'calendar.series.everyWeek': 'Every week on {days}',
  'calendar.series.everyNthWeek': 'Every {weeks} weeks on {days}',
  'calendar.series.interval': 'Repeat',
  'calendar.series.intervalOne': 'Every week',
  'calendar.series.intervalMany': 'Every {weeks} weeks',
  'calendar.series.noEnd': 'No end date.',
  'calendar.series.until': 'Until {date}.',
  'calendar.series.ended': 'This series has ended; nothing new is planned.',
  'calendar.series.upcoming': 'Next sessions',
  'calendar.series.upcomingHint':
    'Untick a session to cancel only that one, for example tomorrow’s training; tick it again to bring it back.',
  'calendar.series.none': 'No sessions planned in the next months.',
  'calendar.series.defaultCancelReason': 'This session is cancelled.',
  'calendar.series.defaultRescheduleReason': 'The training schedule has changed.',
  'calendar.series.ownTime': 'Edited on its own',
  'calendar.series.confirmCancel': 'Cancel this session',
  'calendar.series.confirmRestore': 'Bring this session back',
  'calendar.series.restoreAnyway': 'Restore anyway',
  'calendar.series.keep': 'Leave as it is',
  'calendar.series.notify': 'Notify the invited families (sent once notifications are switched on)',
  'calendar.series.cancelledOne': 'The session is cancelled.',
  'calendar.series.restoredOne': 'The session is back on.',
  'calendar.series.rescheduled':
    'Schedule changed: {kept} sessions kept, {cancelled} cancelled, {created} added.',
  'calendar.series.change': 'Change schedule',
  'calendar.series.changeHint':
    'From the chosen day on, sessions on days that stay keep their answers and attendance and move to the new time; sessions on days that are dropped are cancelled with the reason below; new days are added. Earlier sessions do not change.',
  'calendar.series.fromDate': 'Applies from',
  'calendar.series.removedReason': 'Reason shown on dropped sessions',
  'calendar.series.saveChange': 'Save new schedule',
  'calendar.weekday.1': 'Monday',
  'calendar.weekday.2': 'Tuesday',
  'calendar.weekday.3': 'Wednesday',
  'calendar.weekday.4': 'Thursday',
  'calendar.weekday.5': 'Friday',
  'calendar.weekday.6': 'Saturday',
  'calendar.weekday.7': 'Sunday',
  'calendar.detail.open': 'Open',
  'calendar.detail.close': 'Close',
  'calendar.detail.resource': 'Resource',
  'calendar.detail.arrival': 'Arrive by',
  'calendar.detail.capacity': 'Capacity',
  'calendar.detail.deadline': 'Answers until',
  'calendar.detail.series': 'Part of a recurring series.',
  'calendar.detail.exception': 'This occurrence was edited on its own.',
  'calendar.detail.notReserved':
    'The resource is not reserved yet, so the event cannot be published. Free the slot or choose another resource.',
  'calendar.detail.cancelReason': 'Reason for cancelling',
  'calendar.action.publish': 'Publish',
  'calendar.action.publishAnyway': 'Publish anyway',
  'calendar.action.cancel': 'Cancel event',
  'calendar.action.cancelReason': 'Reason (families will see it)',
  'calendar.action.cancelConfirm': 'Confirm cancelling',
  'calendar.action.cancelFuture': 'Also cancel all later occurrences',
  'calendar.action.complete': 'Mark as completed',
  'calendar.action.edit': 'Edit',
  'calendar.action.save': 'Save changes',
  'calendar.action.discard': 'Cancel editing',
  'calendar.scope.title': 'Apply the change to',
  'calendar.scope.this': 'Only this occurrence',
  'calendar.scope.future': 'This and all later occurrences',
  'calendar.overlap.notice': 'Some athletes are already expected at another event at this time.',
  'calendar.participants.title': 'Participants',
  'calendar.participants.empty':
    'Nobody is expected yet. Participants are added when the event is published.',
  'calendar.participants.answers': 'Answers',
  'calendar.rsvp.yes': 'Yes',
  'calendar.rsvp.no': 'No',
  'calendar.rsvp.tentative': 'Maybe',
  'calendar.rsvp.none': 'No answer',
  'calendar.rsvp.corrected': 'Corrected after the deadline',
  'calendar.rsvp.correct': 'Correct answer',
  'calendar.rsvp.reason': 'Reason (required after the deadline)',
  'calendar.rsvp.save': 'Save answer',
  'calendar.refresh.check': 'Check membership changes',
  'calendar.refresh.upToDate': 'The participants match the teams and groups.',
  'calendar.refresh.toAdd': 'To add',
  'calendar.refresh.toRemove': 'To remove',
  'calendar.refresh.retained': 'Kept because they answered or have attendance recorded',
  'calendar.refresh.apply': 'Apply changes',
  'calendar.attendance.title': 'Attendance',
  'calendar.attendance.present': 'Present',
  'calendar.attendance.late': 'Late',
  'calendar.attendance.absent_excused': 'Excused',
  'calendar.attendance.absent_unexcused': 'Absent',
  'calendar.attendance.not_recorded': 'Not recorded',
  'calendar.attendance.markAll': 'Mark everyone present',
  'calendar.attendance.save': 'Save attendance',
  'calendar.attendance.unsaved': 'Unsaved changes',
  'calendar.attendance.saved': 'Attendance saved.',
  'calendar.attendance.conflict':
    'Someone else changed this row; their entry is shown. Choose again to replace it.',
  'calendar.attendance.conflictSummary': 'Rows changed by someone else:',
  'calendar.attendance.notOpen': 'Attendance can be recorded once the event is published.',
  'calendar.attendance.readOnly': 'You can view attendance but not record it.',
  'calendar.summary.title': 'Attendance rate',
  'calendar.summary.show': 'Show attendance rates for this period',
  'calendar.summary.athlete': 'Athlete',
  'calendar.summary.rate': 'Rate',
  'calendar.summary.total': 'Total',
  'calendar.summary.empty': 'No recorded attendance in this period.',
  'calendar.summary.definition':
    'Attendance rate = (present + late) ÷ (present + late + excused absences + unexcused absences) over the selected period. Cancelled events, rows not recorded and athletes who were not participants are not counted. Excused absences are shown separately.',
  'calendar.family.attending': 'Attending?',
  'calendar.family.answer': 'Answer',
  'calendar.family.closed': 'Answers are closed.',
  'calendar.family.cancelledNotice': 'This event was cancelled.',
  'calendar.venues.title': 'Venues and resources',
  'calendar.venues.intro':
    'A rink with bookable zones is entered as a rink with zones below it. Booking the whole rink blocks all its zones; cleanup time after a booking keeps the next booking away.',
  'calendar.venues.empty': 'No venues yet.',
  'calendar.venue.name': 'Venue name',
  'calendar.venue.add': 'Add venue',
  'calendar.resource.name': 'Resource name',
  'calendar.resource.kind': 'Kind',
  'calendar.resource.parent': 'Part of',
  'calendar.resource.none': 'Nothing (top level)',
  'calendar.resource.surface': 'Surface',
  'calendar.resource.setup': 'Setup minutes before a booking',
  'calendar.resource.cleanup': 'Cleanup minutes after a booking',
  'calendar.resource.add': 'Add resource',
  'calendar.resourceKind.rink': 'Rink',
  'calendar.resourceKind.zone': 'Zone',
  'calendar.resourceKind.room': 'Room',
  'calendar.resourceKind.field': 'Field',
  'calendar.surface.ice': 'Ice',
  'calendar.surface.dryland': 'Dryland',
  'calendar.surface.other': 'Other',
  'error.scheduling.invalidTimeZone': 'This time zone is not valid.',
  'error.scheduling.invalidSchedule':
    'The end must be after the start, and an event cannot last longer than two weeks.',
  'error.scheduling.unknownResource': 'This venue or resource is not available for the event.',
  'error.scheduling.unknownAudience':
    'A team, group or athlete in the audience does not belong to this department.',
  'error.scheduling.unknownStaff': 'Staff must be active coaches of this department.',
  'error.scheduling.unknownVenue': 'This venue does not exist.',
  'error.scheduling.invalidParent': 'The resource this belongs to must be in the same venue.',
  'error.scheduling.alreadyExists': 'An item with this name already exists here.',
  'error.scheduling.resourceConflict':
    'The resource is already booked at this time, including setup and cleanup time. Choose another time or resource.',
  'error.scheduling.staffConflict': 'A coach named here already has another event at this time.',
  'error.scheduling.athleteOverlap':
    'Some athletes are already expected at another published event at this time. Publish anyway to confirm.',
  'error.scheduling.capacityExceeded':
    'The number of athletes is above the capacity of the event or the resource.',
  'error.scheduling.invalidState':
    'The event is no longer in a state that allows this. Reload and try again.',
  'error.scheduling.notEditable': 'A cancelled or completed event can no longer be changed.',
  'error.scheduling.noAudience': 'Choose at least one team, group or athlete before publishing.',
  'error.scheduling.rsvpClosed':
    'The time for answering has passed. Ask a coach to correct the answer.',
  'error.scheduling.rsvpNotAllowed': 'Answers are only accepted for published events.',
  'error.scheduling.rsvpReasonRequired':
    'Give a reason for correcting an answer after the deadline.',
  'error.scheduling.attendanceNotOpen':
    'Attendance can be recorded only for published or completed events.',
  'error.scheduling.seriesInvalid': 'This weekly series cannot be created or changed that way.',
  'error.scheduling.seriesNotOccurrence': 'This event is not part of a weekly series.',
  'error.scheduling.reschedulePast': 'A new schedule can only start today or later.',
  'training.loading': 'Loading…',
  'training.saved': 'Saved.',
  'training.drills.title': 'Drills',
  'training.drills.empty': 'No drills yet.',
  'training.drill.title': 'Title (in the current language)',
  'training.drill.minutes': 'Duration (minutes)',
  'training.drill.equipment': 'Equipment (comma separated)',
  'training.drill.instructions': 'Coaching instructions (staff only)',
  'training.drill.protocol': 'Protocol (optional)',
  'training.drill.add': 'Add drill',
  'training.drill.edit': 'Edit as a new version',
  'training.drill.newVersion': 'Save as a new version',
  'training.drill.version': 'Version',
  'training.drill.department': 'Available to',
  'training.drill.wholeClub': 'The whole club',
  'training.plans.title': 'Training plans',
  'training.plans.empty': 'No plans yet.',
  'training.plan.new': 'New plan',
  'training.plan.open': 'Open',
  'training.plan.close': 'Close',
  'training.plan.state.draft': 'Draft',
  'training.plan.state.published': 'Published',
  'training.plan.state.archived': 'Archived',
  'training.plan.title': 'Plan title',
  'training.plan.objective': 'Objective (optional)',
  'training.plan.sessionMinutes': 'Session length (minutes)',
  'training.plan.prepMinutes': 'Preparation (minutes)',
  'training.plan.breakMinutes': 'Breaks (minutes)',
  'training.plan.override': 'Reason for planning more than fits (optional)',
  'training.plan.active': 'Planned active time',
  'training.plan.available': 'available',
  'training.plan.minutes': 'min',
  'training.plan.fits': 'The plan fits the session.',
  'training.plan.doesNotFit':
    'The plan is longer than the session allows. Shorten it or give a reason.',
  'training.plan.blocks': 'Blocks',
  'training.plan.versions': 'Versions',
  'training.block.objective': 'Block objective',
  'training.block.minutes': 'Minutes',
  'training.block.drill': 'Drill',
  'training.block.noDrill': 'No drill',
  'training.block.intensity': 'Intensity',
  'training.block.equipment': 'Equipment',
  'training.block.surface': 'Surface',
  'training.block.notes': 'Coach notes (never shown to families)',
  'training.block.familyVisible': 'Show a summary to families',
  'training.block.familySummary': 'Summary for families',
  'training.block.add': 'Add block',
  'training.block.remove': 'Remove block',
  'training.block.up': 'Move up',
  'training.block.down': 'Move down',
  'training.block.stationsNote':
    'With parallel stations the block lasts as long as its longest station.',
  'training.station.add': 'Add parallel station',
  'training.station.label': 'Station',
  'training.station.remove': 'Remove station',
  'training.intensity.low': 'Low',
  'training.intensity.medium': 'Medium',
  'training.intensity.high': 'High',
  'training.action.create': 'Create plan',
  'training.action.save': 'Save draft',
  'training.action.publish': 'Publish version',
  'training.action.newDraft': 'Start a new version from this one',
  'training.action.frozen':
    'This version is published and can no longer be changed. Start a new version to make changes.',
  'training.attach.title': 'Training plan',
  'training.attach.none': 'No plan',
  'training.attach.save': 'Use this plan',
  'training.attach.override': 'Reason for using a plan longer than this session',
  'training.attach.help':
    'Only published plan versions can be used. Changing the template later never changes a session that already used it.',
  'calendar.family.plan': 'Plan for the session',
  'error.training.unknownDrill': 'A drill in the plan is not available to this department.',
  'error.training.invalidState': 'This version is no longer a draft.',
  'error.training.draftExists':
    'There is already a draft of this plan. Finish or publish it first.',
  'error.training.activeTimeExceeds':
    'The planned active time is longer than the session allows after preparation and breaks. Shorten the plan or give a reason.',
  'error.training.planDoesNotFit':
    'This plan is longer than the event allows after preparation and breaks. Give a reason to use it anyway.',
  'error.training.planNotPublished': 'Only a published plan version can be used for an event.',
  'error.training.notEditable': 'A published version cannot be changed. Start a new version.',
  'error.assessment.codeTaken': 'This protocol key is already used in your club.',
  'error.assessment.unknownDefinition': 'This protocol does not exist.',
  'error.assessment.unknownDefinitionVersion':
    'A protocol in the session is not available to this department.',
  'error.assessment.unknownAthlete': 'An athlete in the session does not train in this department.',
  'error.assessment.unknownEvent':
    'The chosen event is not an assessment event of this department.',
  'error.assessment.unitMismatch':
    'The unit does not match the protocol. Values are recorded in the protocol’s own unit only.',
  'error.assessment.resultPublished': 'This result is published. Start a correction to change it.',
  'error.assessment.notCorrectable': 'Only a published result can be corrected.',
  'error.assessment.draftExists':
    'This result already has an unpublished correction. Finish or publish it first.',
  'error.assessment.noCorrection': 'This result has no unpublished correction to discard.',
  'error.assessment.nothingToPublish': 'There are no draft results to publish.',
  'error.assessment.notAParticipant': 'This athlete or protocol is not part of the session.',
  'error.assessment.definition.unitRequired': 'Give the unit the values are measured in.',
  'error.assessment.definition.durationUnit': 'A duration is always recorded in seconds.',
  'error.assessment.definition.boundsInvalid':
    'The lowest valid value must be a number that is not above the highest, with no more decimals than the precision.',
  'error.assessment.definition.aggregationNotAllowed':
    'A mean can only be used for numbers and durations.',
  'error.assessment.definition.rubricLevels':
    'A rubric needs between 2 and 10 levels, each with a description.',
  'error.assessment.definition.notApplicable': 'This setting does not apply to this type of value.',
  'error.assessment.value.notANumber': 'Enter a number.',
  'error.assessment.value.tooManyDecimals': 'This has more decimals than the protocol allows.',
  'error.assessment.value.negativeDuration': 'A duration cannot be negative.',
  'error.assessment.value.belowMinimum': 'This is below the lowest valid value.',
  'error.assessment.value.aboveMaximum': 'This is above the highest valid value.',
  'error.assessment.value.notBoolean': 'Use yes or no.',
  'error.assessment.value.notRubricLevel': 'Choose one of the rubric levels.',
  'error.assessment.value.required': 'A valid attempt needs a value; zero is a value.',
  'error.assessment.value.notAllowed': 'An invalid or not attempted try has no value.',
  'error.assessment.value.tooManyAttempts': 'This protocol allows fewer attempts.',
  'error.assessment.value.noAttempts': 'Record at least one attempt.',
  'error.assessment.selection.required': 'Choose the attempt that counts.',
  'error.assessment.selection.invalid': 'The chosen attempt must be a valid one.',
  'error.assessment.selection.notAllowed':
    'This protocol picks the result automatically; do not choose an attempt.',
  'error.assessment.selection.reasonRequired': 'Give a reason for choosing this attempt.',
  'error.assessment.selection.meanNotAllowed': 'A mean can only be used for numbers and durations.',
  'assessments.loading': 'Loading…',
  'assessments.saved': 'Saved.',
  'assessments.protocols.title': 'Protocols',
  'assessments.protocols.empty': 'No protocols yet.',
  'assessments.protocols.add': 'Add protocol',
  'assessments.protocols.newVersion': 'Save as a new version',
  'assessments.protocols.edit': 'Edit as a new version',
  'assessments.protocols.cancel': 'Cancel',
  'assessments.protocol.version': 'Version',
  'assessments.protocol.department': 'Available to',
  'assessments.protocol.wholeClub': 'The whole club',
  'assessments.protocol.code': 'Key (lower-case letters, digits and hyphens)',
  'assessments.protocol.title': 'Title (in the current language)',
  'assessments.protocol.instructions': 'Instructions for the coach (optional)',
  'assessments.protocol.valueType': 'Type of result',
  'assessments.protocol.unit': 'Unit',
  'assessments.protocol.precision': 'Decimal places',
  'assessments.protocol.minimum': 'Lowest valid value',
  'assessments.protocol.maximum': 'Highest valid value',
  'assessments.protocol.direction': 'Better when',
  'assessments.protocol.attempts': 'Attempts allowed',
  'assessments.protocol.aggregation': 'The result counts as',
  'assessments.protocol.allowOutOfRange': 'Accept values outside the lowest and highest value',
  'assessments.protocol.ratioScale': 'Percent change is meaningful for this protocol',
  'assessments.protocol.rubricLevel': 'Description of level',
  'assessments.protocol.addLevel': 'Add level',
  'assessments.protocol.removeLevel': 'Remove last level',
  'assessments.protocol.distance': 'Distance',
  'assessments.protocol.course': 'Course layout',
  'assessments.protocol.surface': 'Surface',
  'assessments.protocol.method': 'Method',
  'assessments.protocol.contextHint':
    'Distance, course, surface and method are kept with every result. Changing any of them starts a new series in the progress charts.',
  'assessments.valueType.numeric': 'Number',
  'assessments.valueType.duration': 'Duration (seconds)',
  'assessments.valueType.boolean': 'Yes or no',
  'assessments.valueType.rubric': 'Rubric (levels)',
  'assessments.direction.lower_is_better': 'Lower is better',
  'assessments.direction.higher_is_better': 'Higher is better',
  'assessments.aggregation.best': 'The best attempt',
  'assessments.aggregation.mean': 'The mean of the valid attempts',
  'assessments.aggregation.last': 'The last valid attempt',
  'assessments.aggregation.coach_selected': 'The attempt the coach chooses',
  'assessments.sessions.title': 'Assessment sessions',
  'assessments.sessions.empty': 'No sessions yet.',
  'assessments.session.new': 'New session',
  'assessments.session.date': 'Date',
  'assessments.session.context': 'Context (optional, staff only)',
  'assessments.session.protocols': 'Protocols used',
  'assessments.session.athletes': 'Participants',
  'assessments.session.search': 'Search athletes',
  'assessments.session.loadMore': 'Load more',
  'assessments.session.team': 'Team',
  'assessments.session.group': 'Training group',
  'assessments.session.rosterChoice': 'Add a team or group',
  'assessments.session.addRoster': 'Add its athletes',
  'assessments.session.selected': 'selected',
  'assessments.session.create': 'Create session',
  'assessments.session.open': 'Open',
  'assessments.session.close': 'Close',
  'assessments.session.participants': 'participants',
  'assessments.session.drafts': 'drafts',
  'assessments.session.published': 'published',
  'assessments.session.noProtocols': 'Add a protocol first.',
  'assessments.session.noAthletes': 'No athletes found.',
  'assessments.entry.protocol': 'Protocol',
  'assessments.entry.athlete': 'Athlete',
  'assessments.entry.attempt': 'Attempt',
  'assessments.entry.invalid': 'Invalid',
  'assessments.entry.blankHint':
    'Leave an attempt empty if it was not made. Zero is a measured value. Mark a spoiled attempt as invalid.',
  'assessments.entry.yes': 'Yes',
  'assessments.entry.no': 'No',
  'assessments.entry.choose': 'Not recorded',
  'assessments.entry.level': 'Level',
  'assessments.entry.counts': 'Counts',
  'assessments.entry.selectedAttempt': 'Attempt that counts',
  'assessments.entry.reason': 'Reason for choosing it',
  'assessments.entry.privateNote': 'Private note (coaches only)',
  'assessments.entry.publicComment': 'Comment for families (shown after publishing)',
  'assessments.entry.save': 'Save results',
  'assessments.entry.unsaved': 'Unsaved changes',
  'assessments.entry.savedRows': 'rows saved',
  'assessments.entry.conflict':
    'Another coach changed this row first. Their entry is shown; check it and save again if needed.',
  'assessments.entry.published': 'Published. Use Correct to change it.',
  'assessments.entry.publish': 'Publish drafts',
  'assessments.entry.publishDone': 'published',
  'assessments.entry.correct': 'Correct',
  'assessments.entry.correctionReason': 'Reason for the correction',
  'assessments.entry.startCorrection': 'Start correction',
  'assessments.entry.cancelCorrection': 'Cancel',
  'assessments.entry.correctionDraft':
    'A correction is in progress. Families still see the published result until you publish it.',
  'assessments.entry.discardCorrection': 'Discard correction',
  'assessments.entry.draftState': 'Draft',
  'assessments.entry.publishedState': 'Published',
  'assessments.entry.noResult': 'No result yet',
  'assessments.status.valid': 'Valid',
  'assessments.status.invalid': 'Invalid',
  'assessments.status.not_attempted': 'Not attempted',
  'assessments.export.button': 'Export results',
  'assessments.export.column.familyName': 'Family name',
  'assessments.export.column.givenName': 'Given name',
  'assessments.export.column.protocol': 'Protocol',
  'assessments.export.column.protocolVersion': 'Protocol version',
  'assessments.export.column.unit': 'Unit',
  'assessments.export.column.status': 'Status',
  'assessments.export.column.value': 'Result',
  'assessments.export.column.revision': 'Revision',
  'assessments.export.column.publishedAt': 'Published',
  'assessments.export.column.comment': 'Comment for families',
  'assessments.aggregates.title': 'Group summary (staff only)',
  'assessments.aggregates.count': 'With a result',
  'assessments.aggregates.suppressed':
    'Fewer than five participants have a result, so no figures are shown.',
  'assessments.aggregates.minimum': 'Lowest',
  'assessments.aggregates.maximum': 'Highest',
  'assessments.aggregates.mean': 'Mean',
  'assessments.progress.title': 'Progress',
  'assessments.progress.athlete': 'Athlete',
  'assessments.progress.choose': 'Choose an athlete',
  'assessments.progress.noAthletes': 'No athletes to show.',
  'assessments.progress.empty': 'No published results yet.',
  'assessments.progress.date': 'Date',
  'assessments.progress.value': 'Result',
  'assessments.progress.version': 'Protocol version',
  'assessments.progress.comment': 'Comment',
  'assessments.progress.noValue': 'No valid value',
  'assessments.progress.change': 'Change since the first result',
  'assessments.progress.absolute': 'Difference',
  'assessments.progress.percent': 'Improvement',
  'assessments.progress.calculation':
    'This is a calculation about the measurements, not an assessment of a child’s worth or potential.',
  'assessments.progress.absoluteOnly': 'Only the difference applies to this protocol.',
  'assessments.progress.gapNote':
    'A gap in the line means no valid value was recorded; it is not zero. Results are joined only when the protocol is the same.',
  'assessments.progress.table': 'Results as a table',
  'assessments.progress.chart': 'Chart of results over time',
  'assessments.progress.context': 'Recorded with',
  'guardianLinks.adult.suspended': 'Guardian access is paused because the athlete turned 18 on',
  'guardianLinks.adult.renew': 'Renew with the athlete’s authorization',
  'guardianLinks.adult.renewedOn': 'Access renewed with the athlete’s authorization on',
  'guardianLinks.adult.title': 'Athletes turning 18',
  'guardianLinks.adult.intro':
    'At 18 a guardian’s access to an athlete pauses until the athlete authorizes it. Invite the athlete to their own account, then renew each link once they agree.',
  'guardianLinks.adult.on': 'Turns 18 on',
  'guardianLinks.adult.days': 'days',
  'guardianLinks.adult.awaiting': 'Links awaiting authorization',
  'guardianLinks.adult.renewed': 'Renewed',
  'guardianLinks.adult.hasAccount': 'Has an account',
  'guardianLinks.adult.invite': 'Invite the athlete',
  'guardianLinks.edit.open': 'Edit',
  'guardianLinks.edit.save': 'Save changes',
  'guardianLinks.edit.cancel': 'Cancel',
  'error.guardianLink.notAdult': 'This athlete is not yet 18, so there is nothing to authorize.',
  'error.guardianLink.alreadyAuthorized':
    'This link has already been renewed with the athlete’s authorization.',
  'teams.rollover.title': 'Plan the next season',
  'teams.rollover.intro':
    'This creates a new draft season with copies of the teams and training groups you keep. The season you are viewing and its rosters stay exactly as they are.',
  'teams.rollover.open': 'Plan the next season',
  'teams.rollover.submit': 'Create the next season',
  'teams.rollover.cancel': 'Cancel',
  'teams.rollover.members': 'Active members',
  'teams.rollover.carryOver':
    'Also place the athletes who are active today on the new teams and groups from the new season’s start',
  'teams.rollover.done': 'Created',
  'teams.rollover.teamsCreated': 'teams',
  'teams.rollover.groupsCreated': 'groups',
  'teams.rollover.placementsCreated': 'placements',
  'athletes.status.archive': 'Archive athlete',
  'athletes.status.withdraw': 'Record withdrawal',
  'athletes.status.reactivate': 'Reactivate athlete',
  'athletes.status.reason': 'Reason',
  'athletes.status.hint.archive':
    'Archiving ends the athlete’s open club membership and team and group placements today. Everything stays as history.',
  'athletes.status.hint.withdraw':
    'Withdrawing does the same and also ends the guardians’ access to this athlete after today. Everything stays as history.',
  'athletes.status.done': 'Status changed.',
  'athletes.status.ended.memberships': 'Memberships ended',
  'athletes.status.ended.teams': 'Team placements ended',
  'athletes.status.ended.groups': 'Group placements ended',
  'athletes.status.ended.links': 'Guardian links ended',
  'error.athlete.statusUnchanged': 'The athlete already has this status.',
  'payments.title': 'Your billing accounts',
  'payments.note':
    'Each account is shown separately. Amounts of different accounts are never added together.',
  'payments.loading': 'Loading your accounts…',
  'payments.empty': 'The club has not shared any billing account with you yet.',
  'payments.statement.show': 'Show statement',
  'payments.statement.hide': 'Hide statement',
  'billing.accounts.title': 'Billing accounts',
  'billing.accounts.loading': 'Loading billing accounts…',
  'billing.accounts.empty': 'No billing accounts yet.',
  'billing.accounts.loadMore': 'Load more accounts',
  'billing.accounts.create.label': 'Account name',
  'billing.accounts.create.submit': 'Create account',
  'billing.accounts.open': 'Open account',
  'billing.accounts.closed': 'Closed',
  'billing.back': 'Back to the accounts',
  'billing.detail.loading': 'Loading the account…',
  'billing.position.balance': 'Balance',
  'billing.position.overdue': 'Overdue',
  'billing.position.upcoming': 'Upcoming',
  'billing.position.credit': 'Unallocated credit',
  'billing.fees.plans.title': 'Fee plans',
  'billing.fees.plans.empty': 'There is no fee plan yet.',
  'billing.fees.plans.create.title': 'New fee plan',
  'billing.fees.plans.create.titleVersion': 'New version of the selected plan',
  'billing.fees.plan.name.en': 'Name (English)',
  'billing.fees.plan.name.hu': 'Name (Hungarian, optional)',
  'billing.fees.plan.name.ro': 'Name (Romanian, optional)',
  'billing.fees.plan.amount': 'Monthly amount',
  'billing.fees.plan.currency': 'Currency',
  'billing.fees.plan.dueDay': 'Due day of the month',
  'billing.fees.plan.dueDayHelp': 'A day beyond the end of a month falls on its last day.',
  'billing.fees.plan.effectiveFrom': 'Effective from',
  'billing.fees.plan.effectiveTo': 'Effective until (optional)',
  'billing.fees.plan.version': 'Version',
  'billing.fees.plan.newVersion': 'New version',
  'billing.fees.plan.startNew': 'Start a new plan instead',
  'billing.fees.plan.submit': 'Save fee plan',
  'billing.fees.plan.saved': 'The fee plan was saved.',
  'billing.fees.plan.invalid': 'Check the amount, the due day and the dates.',
  'billing.fees.run.title': 'Monthly fee run',
  'billing.fees.run.month': 'Month',
  'billing.fees.run.preview': 'Preview the run',
  'billing.fees.run.invalidMonth': 'Choose a month.',
  'billing.fees.run.empty': 'No fee assignment touches this month.',
  'billing.fees.run.count.generate': 'Will post',
  'billing.fees.run.count.review': 'Needs review',
  'billing.fees.run.count.billed': 'Already billed',
  'billing.fees.run.count.skipped': 'Skipped',
  'billing.fees.run.total': 'To post',
  'billing.fees.run.col.athlete': 'Athlete',
  'billing.fees.run.col.account': 'Account',
  'billing.fees.run.col.plan': 'Fee plan',
  'billing.fees.run.col.amount': 'Amount',
  'billing.fees.run.col.due': 'Due',
  'billing.fees.run.col.status': 'Status',
  'billing.fees.run.outcome.generate': 'Will post',
  'billing.fees.run.outcome.review': 'Needs review',
  'billing.fees.run.outcome.billed': 'Already billed',
  'billing.fees.run.outcome.skip': 'Skipped',
  'billing.fees.run.skip.notStarted': 'Not started',
  'billing.fees.run.skip.ended': 'Ended',
  'billing.fees.run.skip.paused': 'Paused',
  'billing.fees.run.skip.planNotEffective': 'Plan not in effect',
  'billing.fees.run.skip.fullyWaived': 'Fully waived',
  'billing.fees.run.skip.accountClosed': 'Account closed',
  'billing.fees.run.skip.periodClosed': 'Reporting period closed',
  'billing.fees.run.review.amount': 'Reviewed amount',
  'billing.fees.run.review.help':
    'These athletes start mid-month. Enter the amount to charge for the first month, or leave it empty to post nothing yet.',
  'billing.fees.run.review.invalid': 'Enter a valid amount.',
  'billing.fees.run.commit': 'Post the run',
  'billing.fees.run.committed': 'The run was posted.',
  'billing.fees.run.result.created': 'New charges',
  'billing.fees.run.result.billed': 'Already billed',
  'billing.fees.run.result.review': 'Awaiting review',
  'billing.fees.run.result.skipped': 'Skipped',
  'billing.fees.auto.title': 'Automatic monthly run',
  'billing.fees.auto.description':
    'When switched on, the system posts the fee charges of the current month by itself, using the club’s own calendar. It posts exactly what the preview shows as “Will post”; assignments that start mid-month stay for your review, and a month that was already billed is never billed again.',
  'billing.fees.auto.on': 'Automatic run is on',
  'billing.fees.auto.off': 'Automatic run is off',
  'billing.fees.auto.enable': 'Switch on',
  'billing.fees.auto.disable': 'Switch off',
  'billing.fees.auto.confirm':
    'Preview the current month first. Once switched on, the system may post this month’s charges within minutes. Switch the automatic run on?',
  'billing.fees.auto.confirmEnable': 'Yes, switch on',
  'billing.fees.auto.cancel': 'Cancel',
  'billing.fees.auto.noRun': 'No automatic run has happened yet.',
  'billing.fees.auto.lastRun': 'Last automatic run',
  'billing.fees.auto.succeeded': 'Succeeded',
  'billing.fees.auto.failed': 'Failed, it is tried again automatically',
  'billing.fees.assign.title': 'Monthly fee plans of this account',
  'billing.fees.assign.empty':
    'No monthly fee plan is assigned to this account. The yearly membership fee is shown in its own section above.',
  'billing.fees.assign.noPlans':
    'The club has no monthly fee plan. You only need one to charge a fee every month; create it under Fee plans & monthly fees on the Billing page.',
  'billing.fees.assign.athlete': 'Athlete',
  'billing.fees.assign.plan': 'Fee plan',
  'billing.fees.assign.startsOn': 'Starts on',
  'billing.fees.assign.submit': 'Assign fee',
  'billing.fees.assign.end': 'End',
  'billing.fees.assign.pause': 'Pause',
  'billing.fees.assign.resume': 'Resume',
  'billing.fees.assign.pausedFrom': 'Pause from',
  'billing.fees.assign.pausedUntil': 'Pause until (optional)',
  'billing.fees.assign.pausedBadge': 'Paused',
  'billing.fees.concession.label': 'Concession',
  'billing.fees.concession.none': 'None',
  'billing.fees.concession.amount': 'Fixed amount off',
  'billing.fees.concession.percent': 'Percentage off',
  'billing.fees.concession.amountValue': 'Amount off',
  'billing.fees.concession.percentValue': 'Percentage off (%)',
  'billing.fees.concession.reason': 'Reason for the approval',
  'billing.fees.concession.invalid': 'Enter a valid value and a reason.',
  'billing.assignments.title': 'Athletes on this account',
  'billing.assignments.empty': 'No athlete is assigned to this account.',
  'billing.assignments.athleteSearch': 'Find an athlete',
  'billing.assignments.noMatches': 'No athlete matches.',
  'billing.assignments.assign': 'Assign athlete',
  'billing.assignments.end': 'End assignment',
  'billing.assignments.until': 'until',
  'billing.access.title': 'People with access to this account',
  'billing.access.empty': 'No one has been approved for this account.',
  'billing.access.personSearch': 'Find a person',
  'billing.access.noMatches': 'No person matches.',
  'billing.access.permission': 'Permission',
  'billing.access.permission.view': 'View balance and statement',
  'billing.access.permission.manage': 'Manage',
  'billing.access.from': 'from',
  'billing.access.validFrom': 'Valid from',
  'billing.access.validUntil': 'Valid until (optional)',
  'billing.access.grant': 'Approve person',
  'billing.access.revoke': 'Revoke access',
  'billing.access.revoked': 'Revoked',
  'billing.charges.title': 'Charges',
  'billing.charges.empty': 'No charges yet.',
  'billing.charges.post.title': 'Post a charge',
  'billing.charges.post.description': 'Description',
  'billing.charges.post.amount': 'Amount',
  'billing.charges.post.dueOn': 'Due date',
  'billing.charges.post.submit': 'Post charge',
  'billing.charge.open': 'Open',
  'billing.charge.paid': 'Settled',
  'billing.charge.reversed': 'Reversed',
  'billing.charge.creditNote': 'Credit note',
  'billing.charge.reverse': 'Reverse charge',
  'billing.kind.fee': 'Fee',
  'billing.kind.manual': 'Manual',
  'billing.kind.opening': 'Opening balance',
  'billing.col.description': 'Description',
  'billing.col.dueOn': 'Due',
  'billing.col.amount': 'Amount',
  'billing.col.outstanding': 'Outstanding',
  'billing.col.status': 'Status',
  'billing.col.date': 'Date',
  'billing.col.method': 'Method',
  'billing.col.reference': 'Reference',
  'billing.col.available': 'Available',
  'billing.col.type': 'Type',
  'billing.col.effectiveOn': 'Effective',
  'billing.col.change': 'Change',
  'billing.col.balance': 'Balance',
  'billing.statement.title': 'Statement',
  'billing.statement.asOf': 'Statement as of',
  'billing.statement.loading': 'Loading the statement…',
  'billing.statement.empty': 'Nothing was recorded on this account up to this day.',
  'billing.statement.opening': 'Balance brought forward',
  'billing.statement.closing': 'Balance on this day',
  'billing.statement.truncated': 'Older entries are included in the balance brought forward.',
  'billing.statement.note':
    'Entries count from the moment they were recorded. A positive change increases what is owed; a negative change lowers it. Overdue, upcoming and credit are shown as they stood on the chosen day.',
  'billing.statement.openCharges': 'Charges still to pay on this day',
  'billing.statement.kind.charge': 'Charge',
  'billing.statement.kind.creditNote': 'Credit note',
  'billing.statement.kind.payment': 'Payment',
  'billing.statement.kind.paymentReversal': 'Payment reversed',
  'billing.statement.kind.refund': 'Refund',
  'billing.periods.title': 'Closed periods',
  'billing.periods.note':
    'Closing a period locks the ledger through a day: no charge due and no payment paid on or before that day can be posted afterwards, by hand or by the fee run. A close cannot be undone. Later corrections are posted as credit notes, reversals or refunds in the open period.',
  'billing.periods.closedThrough': 'Closed through',
  'billing.periods.none': 'No period has been closed yet.',
  'billing.periods.field.closedThrough': 'Close through (inclusive)',
  'billing.periods.field.note': 'Reason or note',
  'billing.periods.field.confirm': 'I understand this cannot be undone',
  'billing.periods.submit': 'Close period',
  'billing.periods.done': 'The period was closed.',
  'billing.periods.history': 'Earlier closes',
  'billing.periods.closedAt': 'Closed on',
  'billing.reports.title': 'Reports',
  'billing.reports.note':
    'Totals for the whole club as the ledger stood at the end of the chosen day, one block per currency; currencies are never added together. Overdue is what is still owed on charges due before that day. Charges are grouped by the team the athlete was on at the charge’s due date, and payments by method.',
  'billing.reports.asOf': 'Report as of',
  'billing.reports.asOfHelp': 'Leave empty for today.',
  'billing.reports.season': 'Season',
  'billing.reports.team': 'Team at the due date',
  'billing.reports.method': 'Payment method',
  'billing.reports.account': 'Account',
  'billing.reports.any': 'All',
  'billing.reports.loading': 'Loading the report…',
  'billing.reports.empty': 'There are no billing accounts for this selection.',
  'billing.reports.accounts': 'Accounts',
  'billing.reports.generatedAt': 'Generated',
  'billing.reports.currency': 'Currency',
  'billing.reports.byTeam': 'Charges by team at the due date',
  'billing.reports.byMethod': 'Payments by method',
  'billing.reports.noTeam': 'No team on the due date',
  'billing.reports.total': 'Total',
  'billing.reports.col.team': 'Team',
  'billing.reports.col.method': 'Method',
  'billing.reports.col.count': 'Entries',
  'billing.reports.col.charged': 'Charged',
  'billing.reports.col.credited': 'Credit notes',
  'billing.reports.col.allocated': 'Allocated',
  'billing.reports.col.received': 'Received',
  'billing.reports.col.reversed': 'Reversed',
  'billing.reports.col.refunded': 'Refunded',
  'billing.reports.chargesOmitted':
    'Charges are not shown while a payment method is chosen, because a charge has no payment method.',
  'billing.reports.paymentsOmitted':
    'Payments are not shown while a team is chosen, because a payment belongs to an account, not to a team.',
  'billing.reports.reconciliation.title': 'Reconciliation',
  'billing.reports.reconciliation.note':
    'Compares the charge, payment, allocation, refund and statement views of the same ledger. It covers every entry of the selected accounts up to this day, whatever season, team or method is chosen.',
  'billing.reports.reconciliation.check': 'Check',
  'billing.reports.reconciliation.expected': 'Expected',
  'billing.reports.reconciliation.actual': 'Found',
  'billing.reports.reconciliation.result': 'Result',
  'billing.reports.reconciliation.ok': 'Agrees',
  'billing.reports.reconciliation.mismatch': 'Differs',
  'billing.reports.check.balance':
    'The balance from charges and payments equals the sum of the statement entries',
  'billing.reports.check.settle':
    'What is owed on charges equals the outstanding total (no charge is settled beyond its amount)',
  'billing.reports.check.allocations':
    'Allocations seen from the charges equal allocations seen from the payments',
  'billing.reports.check.payments':
    'Retained payments equal allocated plus refunded plus unallocated credit',
  'billing.reports.export': 'Download CSV',
  'billing.reason': 'Reason',
  'billing.confirm': 'Confirm',
  'billing.cancel': 'Cancel',
  'billing.creditNote.amount': 'Credit amount',
  'billing.creditNote.submit': 'Post credit note',
  'billing.reverse.submit': 'Reverse',
  'billing.refund.amount': 'Refund amount',
  'billing.refund.submit': 'Post refund',
  'billing.payments.title': 'Payments',
  'billing.payments.empty': 'No payments yet.',
  'billing.payment.post.title': 'Record a payment',
  'billing.payment.amount': 'Amount received',
  'billing.payment.paidOn': 'Date received',
  'billing.payment.method': 'Method',
  'billing.payment.reference': 'Receipt or bank reference',
  'billing.payment.payerReference': 'Payer (optional)',
  'billing.payment.preview': 'Preview allocation',
  'billing.payment.allocation.title': 'Allocation, oldest due first. Change the amounts if needed.',
  'billing.payment.allocation.none':
    'The account has no open charge. The whole amount stays as unallocated credit.',
  'billing.payment.allocation.invalidAmount':
    'Enter each allocated amount as a positive number with at most two decimals, or leave it empty.',
  'billing.payment.allocation.exceedsPayment':
    'The allocated amounts add up to more than the payment.',
  'billing.payment.allocation.total': 'Allocated',
  'billing.payment.allocation.unallocated': 'Stays as unallocated credit',
  'billing.payment.submit': 'Record payment',
  'billing.payment.reversed': 'Reversed',
  'billing.payment.refunded': 'Refunded',
  'billing.payment.reverse': 'Reverse payment',
  'billing.payment.refund': 'Refund',
  'billing.payment.allocate': 'Allocate credit',
  'billing.payment.allocate.submit': 'Allocate',
  'billing.payment.allocate.nothingOpen':
    'The account has no open charge to allocate this credit to.',
  'billing.method.cash': 'Cash',
  'billing.method.bankTransfer': 'Bank transfer',
  'billing.method.cardTerminal': 'Card terminal',
  'billing.method.openingBalance': 'Opening balance',
  'billing.method.other': 'Other',
  'billing.invalidAmount': 'Enter a positive amount with at most two decimals, for example 200.50.',
  'billing.posted': 'Posted.',
  'teams.category.U7': 'U7',
  'teams.category.U9': 'U9',
  'teams.category.WOMEN': 'Women',
  'teams.category.GIRLS_3X3_CUP': 'Girls 3x3 Cup',
  'competitions.seasons.title': 'Competition seasons',
  'competitions.seasons.empty': 'No competition season has been created yet.',
  'competitions.seasons.create.title': 'New competition season',
  'competitions.seasons.submit': 'Create competition season',
  'competitions.seasons.done': 'Competition season created.',
  'competitions.field.season': 'Season',
  'competitions.field.name': 'Name',
  'competitions.field.federation': 'Federation',
  'competitions.field.category': 'Category',
  'competitions.field.division': 'Division (optional)',
  'competitions.field.format': 'Format (optional)',
  'competitions.field.phases': 'Phases',
  'competitions.phase.regular': 'Regular season',
  'competitions.phase.playoff': 'Playoff',
  'competitions.phase.tournament': 'Tournament',
  'competitions.rules.none': 'No active rule version: eligibility stays “needs review”.',
  'competitions.rules.active': 'Active rule version',
  'competitions.profiles.title': 'Rule profile versions',
  'competitions.profiles.empty':
    'This competition has no rule profile yet. Until one is activated, every eligibility result is “needs review”.',
  'competitions.profiles.catalogField': 'Start a draft from',
  'competitions.profiles.createDraft': 'Create draft',
  'competitions.profiles.draftCreated': 'Draft created.',
  'competitions.profile.version': 'Version',
  'competitions.profile.status.draft': 'Draft',
  'competitions.profile.status.active': 'Active',
  'competitions.profile.status.superseded': 'Superseded',
  'competitions.profile.validity': 'Valid',
  'competitions.profile.counts':
    'sources: {sources}, hashes recorded: {hashes}, conditions: {conditions}, manual checks: {manual}',
  'competitions.profile.checkedOn': 'Sources checked on',
  'competitions.profile.activatedOn': 'Activated on',
  'competitions.profile.draftNote':
    'A draft never approves anyone. Record the check of the source documents, then activate it.',
  'competitions.profile.manualChecks':
    'Mandatory manual checks (not encoded; they stay open and block an official-ready export)',
  'competitions.verify.title': 'Record the source check',
  'competitions.verify.hashField': 'SHA-256 of source',
  'competitions.verify.checkedOn': 'Day you checked the sources',
  'competitions.verify.note': 'What you checked (amendments, the club’s competition)',
  'competitions.verify.submit': 'Record the source check',
  'competitions.verify.done': 'Source check recorded.',
  'competitions.verify.problem.hash': 'A hash is 64 hexadecimal characters.',
  'competitions.verify.problem.date': 'Enter a valid date.',
  'competitions.verify.problem.future': 'The check date cannot be in the future.',
  'competitions.verify.problem.note': 'Write what you checked.',
  'competitions.activate.title': 'Activate this version',
  'competitions.activate.note': 'Reason for activating',
  'competitions.activate.confirm':
    'I checked the exact source documents and this club’s competition against this version.',
  'competitions.activate.submit': 'Activate version',
  'competitions.activate.done': 'Version activated. The previous active version is superseded.',
  'competitions.blocker.no_sources':
    'The profile names no source, so there is nothing to verify and it cannot be activated.',
  'competitions.blocker.source_hash_missing': 'Record the SHA-256 of every source document.',
  'competitions.blocker.sources_not_checked':
    'Record the day you checked the sources for amendments.',
  'competitions.blocker.category_mismatch':
    'The profile category differs from the competition category.',
  'error.competition.invalidTimeZone': 'This is not a known time zone.',
  'error.competition.seasonClosed': 'The season is closed; competitions cannot be added to it.',
  'error.ruleProfile.unknownCatalogKey': 'This draft is not in the catalog.',
  'error.ruleProfile.categoryMismatch':
    'The draft belongs to a different category than the competition.',
  'error.ruleProfile.draftExists':
    'This competition already has a draft; verify and activate it first.',
  'error.ruleProfile.notDraft': 'Only a draft can be changed.',
  'error.ruleProfile.unknownSource': 'A hash was given for a source this profile does not name.',
  'error.ruleProfile.checkedInFuture': 'The check date cannot be in the future.',
  'error.ruleProfile.notReady': 'Record every source hash and the check date before activating.',
  'registrations.title': 'Registrations',
  'registrations.loading': 'Loading registrations…',
  'registrations.empty': 'No athlete is registered in this competition yet.',
  'registrations.openEnded': 'no end day',
  'registrations.license': 'Licence reference',
  'registrations.endedReason': 'Reason for ending',
  'registrations.mode.native': 'Native',
  'registrations.mode.temporary_play_up': 'Temporary play-up',
  'registrations.mode.exclusive_higher': 'Exclusive higher registration',
  'registrations.mode.hint':
    'Native: the athlete plays in their own category. Temporary play-up: the athlete plays up for a limited time. Exclusive higher: the athlete is registered only in the higher category.',
  'registrations.status.active': 'Active',
  'registrations.status.ended': 'Ended',
  'registrations.create.title': 'Register an athlete',
  'registrations.field.athlete': 'Athlete (type a name)',
  'registrations.field.team': 'Team',
  'registrations.field.mode': 'Registration mode',
  'registrations.field.validFrom': 'First day',
  'registrations.field.validTo': 'Last day (optional)',
  'registrations.field.license': 'Licence reference (optional)',
  'registrations.field.classification': 'Competition classification',
  'registrations.classification.hint':
    'Only what this competition’s rules need: girls for the girls’ birth-date range, national-team female for players who need the competition commission’s approval, general for everyone else.',
  'registrations.classification.general': 'General',
  'registrations.classification.girls': 'Girls',
  'registrations.classification.national_team_female': 'National-team female player',
  'registrations.noAthletes': 'No athlete found',
  'registrations.noTeams':
    'This competition’s department has no team in the season yet. Create one on the Teams page first.',
  'registrations.submit': 'Register athlete',
  'registrations.done': 'Athlete registered.',
  'registrations.correct': 'Correct',
  'registrations.correct.submit': 'Save correction',
  'registrations.correct.done': 'Registration corrected.',
  'registrations.end': 'End registration',
  'registrations.end.hint':
    'The registration stays as history. To register the athlete again, create a new registration.',
  'registrations.end.day': 'Last day of the registration',
  'registrations.end.reason': 'Reason for ending',
  'registrations.end.submit': 'End registration',
  'registrations.end.done': 'Registration ended.',
  'registrations.cancel': 'Cancel',
  'error.registration.overlap':
    'This athlete already has an active registration in this competition on some of these days.',
  'error.registration.athleteNotActive': 'Only an active athlete can be registered.',
  'error.registration.teamMismatch':
    'Choose an active team of this competition’s department and season.',
  'error.registration.notActive': 'This registration has ended and can no longer be changed.',
  'error.registration.invalidEnd':
    'The last day cannot be before the first day, in the future, or after the planned last day.',
  'error.registration.invalidValidTo': 'The last day cannot be before the first day.',
  'evidence.title': 'Documentary evidence',
  'evidence.hint':
    'Record that a document exists for an athlete, who issued it and the days it is valid, then verify or reject it. Only verified evidence counts towards eligibility. No copy of the document is stored here.',
  'evidence.loading': 'Loading evidence…',
  'evidence.empty': 'No evidence is recorded for the athletes of this competition yet.',
  'evidence.openEnded': 'no end day',
  'evidence.issuer': 'Issuer',
  'evidence.reference': 'Reference',
  'evidence.decisionNote': 'Note on the decision',
  'evidence.type.medical_clearance': 'Medical clearance',
  'evidence.type.under_18_agreement': 'Agreement for an athlete under 18',
  'evidence.type.promotion_agreement': 'Play-up agreement',
  'evidence.type.loan_agreement': 'Loan agreement',
  'evidence.type.transfer_approval': 'Transfer approval',
  'evidence.status.pending': 'Waiting for a decision',
  'evidence.status.verified': 'Verified',
  'evidence.status.rejected': 'Rejected',
  'evidence.create.title': 'Record evidence',
  'evidence.field.athlete': 'Athlete (type a name)',
  'evidence.field.type': 'Document type',
  'evidence.field.issuer': 'Issuer (optional)',
  'evidence.field.reference': 'Reference (optional)',
  'evidence.field.validFrom': 'First valid day',
  'evidence.field.validTo': 'Last valid day (optional)',
  'evidence.noAthletes': 'No athlete found',
  'evidence.submit': 'Record evidence',
  'evidence.done': 'Evidence recorded. It counts once it is verified.',
  'evidence.correct': 'Correct',
  'evidence.correct.submit': 'Save correction',
  'evidence.correct.done': 'Evidence corrected.',
  'evidence.verify': 'Verify',
  'evidence.verify.hint':
    'Confirm that you have seen the document and that the details match. A verified record can no longer be corrected, only withdrawn.',
  'evidence.verify.note': 'Note (optional, do not write health details)',
  'evidence.verify.submit': 'Verify evidence',
  'evidence.verify.done': 'Evidence verified.',
  'evidence.reject': 'Reject',
  'evidence.withdraw': 'Withdraw',
  'evidence.reject.hint':
    'The record stays as history and can no longer change. If a valid document exists, record it as new evidence.',
  'evidence.reject.reason': 'Reason (do not write health details)',
  'evidence.reject.submit': 'Confirm',
  'evidence.reject.done': 'Evidence rejected.',
  'evidence.cancel': 'Cancel',
  'error.evidence.wrongState':
    'This evidence has already been decided or withdrawn and cannot be changed this way.',
  'error.evidence.invalidInterval': 'The last valid day cannot be before the first valid day.',
  'suspension.title': 'Suspensions',
  'suspension.hint':
    'Record that an athlete may not play, until a last day or for a number of games. Only an active suspension counts towards eligibility; a served or revoked one stays as history.',
  'suspension.loading': 'Loading suspensions…',
  'suspension.empty': 'No suspension applies to this competition.',
  'suspension.until': 'until',
  'suspension.gamesLeft': 'games left',
  'suspension.everyCompetition': 'every competition of the club',
  'suspension.reference': 'Decision reference',
  'suspension.closingNote': 'Note on the closing',
  'suspension.status.active': 'Active',
  'suspension.status.served': 'Served',
  'suspension.status.revoked': 'Revoked',
  'suspension.create.title': 'Record a suspension',
  'suspension.field.athlete': 'Athlete (type a name)',
  'suspension.field.scope': 'Applies in',
  'suspension.field.measure': 'Counted in',
  'suspension.measure.games': 'Games',
  'suspension.measure.days': 'Days (until a last day)',
  'suspension.field.startsOn': 'First day',
  'suspension.field.endsOn': 'Last day',
  'suspension.field.games': 'Games left',
  'suspension.field.gamesHint': 'A whole number from 0 to {maximum}.',
  'suspension.field.reference': 'Decision reference (optional)',
  'suspension.noAthletes': 'No athlete found',
  'suspension.submit': 'Record suspension',
  'suspension.done': 'Suspension recorded. It counts while it is active.',
  'suspension.correct': 'Correct',
  'suspension.correct.submit': 'Save correction',
  'suspension.correct.done': 'Suspension corrected.',
  'suspension.serve': 'Mark served',
  'suspension.serve.hint':
    'Confirm that the suspension has been served. It stays as history and no longer counts.',
  'suspension.serve.note': 'Note (optional)',
  'suspension.serve.submit': 'Mark served',
  'suspension.serve.done': 'Suspension marked as served.',
  'suspension.revoke': 'Revoke',
  'suspension.revoke.hint':
    'The suspension stays as history, no longer counts and can no longer change. Use this when the decision was annulled or recorded by mistake.',
  'suspension.revoke.reason': 'Reason',
  'suspension.revoke.submit': 'Confirm',
  'suspension.revoke.done': 'Suspension revoked.',
  'suspension.cancel': 'Cancel',
  'error.suspension.wrongState':
    'This suspension has already been served or revoked and cannot be changed this way.',
  'error.suspension.invalidInterval': 'The last day cannot be before the first day.',
  'error.suspension.wrongMeasure':
    'A suspension counted in days cannot be given a number of games, and the other way round.',
  'match.title': 'Matches',
  'match.hint':
    'Attach a match event of the department to this competition with the club’s team, a phase and the opponent. Completing a match records the rule profile version that was active; a completed or cancelled match stays as history.',
  'match.loading': 'Loading matches…',
  'match.empty': 'No match is recorded for this competition.',
  'match.status.scheduled': 'Scheduled',
  'match.status.completed': 'Completed',
  'match.status.cancelled': 'Cancelled',
  'match.homeAway.home': 'Home',
  'match.homeAway.away': 'Away',
  'match.eventCancelled':
    'The calendar event of this match was cancelled; the match cannot be completed.',
  'match.ruleVersion': 'Rule profile version used',
  'match.noRuleVersion': 'No rule profile was active when it was completed.',
  'match.cancelReason': 'Reason',
  'match.create.title': 'Record a match',
  'match.field.event': 'Match event',
  'match.field.team': 'Team',
  'match.field.phase': 'Phase',
  'match.field.opponent': 'Opponent',
  'match.field.opponentHint': 'A club name, up to 100 characters.',
  'match.field.homeAway': 'Home or away',
  'match.noEvents':
    'No free match event in this season. Create a match event in the Calendar first.',
  'match.noTeams': 'No active team of this department and season exists.',
  'match.submit': 'Record match',
  'match.done': 'Match recorded.',
  'match.correct': 'Correct',
  'match.correct.submit': 'Save correction',
  'match.correct.done': 'Match corrected.',
  'match.complete': 'Mark completed',
  'match.complete.hint':
    'Confirm that the match was played. The rule profile version active now is recorded with the match, and the match can no longer change.',
  'match.complete.submit': 'Mark completed',
  'match.complete.done': 'Match marked as completed.',
  'match.cancel': 'Cancel match',
  'match.cancel.hint':
    'The match stays as history and can no longer change. Its event becomes free for another match.',
  'match.cancel.reason': 'Reason',
  'match.cancel.submit': 'Confirm',
  'match.cancel.done': 'Match cancelled.',
  'match.close': 'Close',
  'error.match.wrongState':
    'This match has already been completed or cancelled and cannot be changed this way.',
  'error.match.eventTaken': 'Another match already uses this event.',
  'error.match.invalidEvent': 'Choose an uncancelled match event of this competition’s department.',
  'error.match.invalidTeam':
    'The team must be an active team of the competition’s department and season.',
  'error.match.invalidPhase': 'The phase must be one of the phases of the competition.',
  'error.match.notPlayedYet':
    'The day of this match has not come yet, so it cannot be marked as completed.',
  'error.match.eventCancelled':
    'The calendar event of this match was cancelled, so the match cannot be completed.',
  'roster.open': 'Roster',
  'roster.title': 'Roster',
  'roster.hint':
    'The athletes selected for this match. Selecting an athlete does not check eligibility (use Check eligibility below) and does not prove that the athlete played.',
  'roster.loading': 'Loading the roster…',
  'roster.empty': 'No athlete is selected for this match yet.',
  'roster.locked': 'This match is no longer scheduled, so its roster cannot change.',
  'roster.role.goalkeeper': 'Goalkeeper',
  'roster.role.defender': 'Defender',
  'roster.role.forward': 'Forward',
  'roster.noShirt': 'no number',
  'roster.shirt': 'Shirt',
  'roster.field.athlete': 'Athlete',
  'roster.field.role': 'Role in this match',
  'roster.field.shirt': 'Shirt number (0–99)',
  'roster.noAthletes': 'No athlete matches.',
  'roster.add': 'Add to roster',
  'roster.add.done': 'Athlete added to the roster.',
  'roster.edit': 'Change',
  'roster.edit.submit': 'Save',
  'roster.edit.done': 'Roster entry saved.',
  'roster.remove': 'Remove',
  'roster.remove.done': 'Athlete removed from the roster.',
  'roster.close': 'Close',
  'roster.status.draft': 'Draft',
  'roster.status.finalized': 'Finalized',
  'roster.finalizedAt': 'Finalized {finalizedAt}.',
  'roster.acknowledged': 'Open review items were acknowledged when it was finalized.',
  'roster.frozen':
    'This roster is finalized. Reopen it to change it or to check eligibility again.',
  'roster.finalize': 'Finalize roster',
  'roster.finalize.hint':
    'Every athlete is checked again first. Finalizing is refused when an athlete is not eligible or the roster breaks a rule.',
  'roster.finalize.acknowledgement': 'Statement about what still needs review',
  'roster.finalize.acknowledgement.help':
    'Needed when anything on the roster still needs review. Say that you reviewed it and how.',
  'roster.finalize.submit': 'Finalize',
  'roster.finalize.done': 'Roster finalized.',
  'roster.reopen': 'Reopen roster',
  'roster.reopen.reason': 'Reason for reopening',
  'roster.reopen.submit': 'Reopen',
  'roster.reopen.done': 'Roster reopened as a draft.',
  'manualCheck.hint':
    'Record a check as done only after you have done it yourself; a recorded check counts for this roster and this rule version only.',
  'manualCheck.open': 'open',
  'manualCheck.done': 'done, checked on {checkedOn}',
  'manualCheck.record': 'Record as done',
  'manualCheck.record.submit': 'Record',
  'manualCheck.statement': 'What was checked',
  'manualCheck.statement.hint': 'Do not write health details or other personal data.',
  'manualCheck.checkedOn': 'Day checked',
  'manualCheck.withdraw': 'Withdraw record',
  'manualCheck.withdraw.reason': 'Reason for withdrawing',
  'manualCheck.withdraw.submit': 'Withdraw',
  'manualCheck.close': 'Cancel',
  'error.manualCheck.alreadyRecorded':
    'This check is already recorded for this roster and rule version.',
  'error.manualCheck.notRecorded': 'This check is not recorded, so there is nothing to withdraw.',
  'error.manualCheck.noActiveRules':
    'The competition has no active rule version, so no check can be recorded.',
  'error.manualCheck.futureDay': 'The day checked cannot be in the future.',
  'externalHistory.title': 'Matches elsewhere',
  'externalHistory.hint':
    'Verified matches an athlete played outside this club’s rosters count towards the daily and weekly limits. Record each match, then attest that the history is complete; without an attestation the athlete stays at Needs review wherever the rules limit matches.',
  'externalHistory.field.athlete': 'Athlete (type a name)',
  'externalHistory.noAthletes': 'No athlete found',
  'externalHistory.loading': 'Loading…',
  'externalHistory.appearances.title': 'Matches played elsewhere',
  'externalHistory.appearances.empty': 'No match elsewhere is recorded for this athlete.',
  'externalHistory.appearance.status.recorded': 'Recorded',
  'externalHistory.appearance.status.withdrawn': 'Withdrawn',
  'externalHistory.appearance.source': 'Verified at',
  'externalHistory.appearance.create.title': 'Record a match played elsewhere',
  'externalHistory.appearance.field.playedOn': 'Day played',
  'externalHistory.appearance.field.competition': 'Competition or club',
  'externalHistory.appearance.field.source': 'Where it was verified',
  'externalHistory.appearance.field.sourceHint':
    'For example a match sheet number or a federation page. Do not write personal data.',
  'externalHistory.appearance.submit': 'Record match',
  'externalHistory.appearance.done':
    'Match recorded. It counts towards the daily and weekly limits.',
  'externalHistory.attestations.title': 'Completeness attestations',
  'externalHistory.attestations.empty': 'No attestation is recorded for this athlete.',
  'externalHistory.attestation.status.granted': 'Granted',
  'externalHistory.attestation.status.withdrawn': 'Withdrawn',
  'externalHistory.attestation.covers': 'Complete from {from} through {through}',
  'externalHistory.attestation.create.title': 'Attest that the history is complete',
  'externalHistory.attestation.hint':
    'You state that every verified match this athlete played outside the club between these days is recorded above. It counts for a match only when it reaches back over the longest limit window of the rules and up to the day before the match.',
  'externalHistory.attestation.field.from': 'Complete from',
  'externalHistory.attestation.field.through': 'Complete through (not in the future)',
  'externalHistory.attestation.field.statement': 'What you checked',
  'externalHistory.attestation.submit': 'Attest completeness',
  'externalHistory.attestation.done': 'Attestation recorded.',
  'externalHistory.withdraw': 'Withdraw',
  'externalHistory.withdraw.reason': 'Reason for withdrawing',
  'externalHistory.withdraw.submit': 'Confirm withdrawal',
  'externalHistory.withdraw.done': 'Record withdrawn.',
  'externalHistory.withdrawnReason': 'Reason for withdrawal',
  'externalHistory.cancel': 'Cancel',
  'error.externalHistory.futureDay': 'The day cannot be in the future.',
  'error.externalHistory.wrongState': 'This record was withdrawn already and cannot change.',
  'roster.export': 'Export roster (CSV)',
  'roster.export.hint':
    'Downloads this roster for internal use. It is an internal aid and never a submission to the federation.',
  'roster.export.ready':
    'Official-ready: every check passed. The file is still an internal aid, not a submission.',
  'roster.export.blocked': 'Not official-ready, so the file is a draft. Reasons:',
  'roster.export.hidden': '{count} more athletes outside your scope are not in the file.',
  'roster.export.blocker.PROFILE_NOT_ACTIVE': 'The competition has no active rule version.',
  'roster.export.blocker.ROSTER_NOT_FINALIZED': 'The roster is not finalized.',
  'roster.export.blocker.ENTRIES_NOT_VISIBLE':
    'Some athletes on the roster are outside your scope, so the file cannot list it in full.',
  'roster.export.blocker.ROSTER_NOT_ELIGIBLE':
    'An athlete or the roster as a whole is not eligible or still needs review.',
  'roster.export.blocker.MANUAL_CHECK_UNRESOLVED':
    'The manual check “{key}” is not recorded as done.',
  'roster.export.file.label': 'Internal aid, not an official submission',
  'roster.export.file.competition': 'Competition',
  'roster.export.file.opponent': 'Opponent',
  'roster.export.file.matchDay': 'Match day',
  'roster.export.file.status': 'Roster status',
  'roster.export.file.officialReady': 'Official-ready',
  'roster.export.file.yes': 'yes',
  'roster.export.file.no': 'no',
  'roster.export.file.blocker': 'Reason it is not official-ready',
  'roster.export.column.shirt': 'Shirt number',
  'roster.export.column.familyName': 'Family name',
  'roster.export.column.givenName': 'Given name',
  'roster.export.column.role': 'Role',
  'roster.export.column.outcome': 'Eligibility',
  'eligibility.title': 'Eligibility',
  'eligibility.hint':
    'Checks every athlete on this roster and the roster as a whole against the competition’s active rule version, and saves the result. It covers only the automated rules; the manual checks listed below stay with you.',
  'eligibility.loading': 'Loading the eligibility check…',
  'eligibility.none': 'This roster has not been checked yet.',
  'eligibility.evaluate': 'Check eligibility',
  'eligibility.evaluate.again': 'Check again',
  'eligibility.evaluate.done': 'Eligibility checked and saved.',
  'eligibility.checked':
    'Checked {checkedAt} for the match day {matchDay} with rule version {version}.',
  'eligibility.checkedWithoutRules':
    'Checked {checkedAt} for the match day {matchDay} without an active rule version.',
  'eligibility.roster': 'Roster as a whole:',
  'eligibility.outcome.ELIGIBLE': 'Eligible',
  'eligibility.outcome.INELIGIBLE': 'Not eligible',
  'eligibility.outcome.REVIEW_REQUIRED': 'Needs review',
  'eligibility.stale': 'Something has changed since this check. Check again.',
  'eligibility.decisionStale': 'out of date',
  'eligibility.removed': 'no longer on the roster',
  'eligibility.rosterChanged': 'The roster has changed since this check. Check again.',
  'eligibility.rulesChanged': 'Another rule version is active now. Check again.',
  'eligibility.hidden': '{count} more athletes outside your scope were also checked.',
  'eligibility.manualChecks': 'Manual checks that stay with you:',
  'eligibility.source': 'source',
  'eligibility.exception': 'resolved by exception {reference}',
  'eligibility.reason.unknown': 'Reason {code}.',
  'eligibility.reason.PROFILE_MISSING':
    'The competition has no active rule version, so this needs review.',
  'eligibility.reason.PROFILE_NOT_ACTIVE':
    'The rule version is {status}, not active, so this needs review.',
  'eligibility.reason.PROFILE_NOT_VALID_ON_DATE':
    'The active rule version does not cover the match day.',
  'eligibility.reason.BIRTH_DATE_AFTER_MATCH': 'The date of birth is after the match day.',
  'eligibility.reason.REGISTRATION_MISSING': 'The athlete is not registered in this competition.',
  'eligibility.reason.REGISTRATION_NOT_VALID':
    'The registration in this competition has ended or does not cover the match day.',
  'eligibility.reason.LICENSE_REFERENCE_MISSING': 'The registration has no licence reference.',
  'eligibility.reason.CLASSIFICATION_NOT_PERMITTED':
    'The classification “{classification}” is not permitted in this competition.',
  'eligibility.reason.AGE_BELOW_MINIMUM':
    'Age {age} on the match day is below the minimum age of {minimumAge}.',
  'eligibility.reason.EXCLUSIVE_HIGHER_REGISTRATION':
    'The athlete is registered exclusively in the higher category {higherCategory}.',
  'eligibility.reason.BIRTH_DATE_ROUTE_NOT_DEFINED':
    'The rules define no birth-date range for this registration, so this needs review.',
  'eligibility.reason.AGE_EXCEPTION_NOT_SUPPORTED':
    'The rules of this competition do not allow the registration mode “{registrationMode}”.',
  'eligibility.reason.BIRTH_DATE_OUTSIDE_PERMITTED_RANGE':
    'The date of birth is outside the range permitted for the registration mode “{registrationMode}”.',
  'eligibility.reason.DOCUMENT_MISSING': 'No {documentType} is recorded.',
  'eligibility.reason.DOCUMENT_NOT_VERIFIED': 'The {documentType} is not verified.',
  'eligibility.reason.DOCUMENT_EXPIRED': 'The {documentType} expired before the match day.',
  'eligibility.reason.DOCUMENT_NOT_YET_VALID':
    'The {documentType} is not yet valid on the match day.',
  'eligibility.reason.SUSPENDED': 'An active suspension covers this match.',
  'eligibility.reason.ATTESTATION_MISSING': 'A required approval is not recorded.',
  'eligibility.reason.ATTESTATION_DENIED': 'A required approval was refused.',
  'eligibility.reason.TEAM_CHANGE_OUTSIDE_WINDOW':
    'A move between the club’s teams is not allowed in this phase.',
  'eligibility.reason.EXTERNAL_HISTORY_INCOMPLETE':
    'Matches played outside this club are not recorded, so the match limits need review.',
  'eligibility.reason.DAILY_LIMIT_EXCEEDED':
    '{count} matches on the match day; the {profileCategory} rules allow {maximum}.',
  'eligibility.reason.ROLLING_WINDOW_LIMIT_EXCEEDED':
    '{count} matches within {windowDays} days; the {profileCategory} rules allow {maximum}.',
  'eligibility.reason.PLAYOFF_PARTICIPATION_UNCONFIRMED':
    'Playoff participation counts only once a club administrator has confirmed what counts.',
  'eligibility.reason.PLAYOFF_PARTICIPATION_UNKNOWN': 'Playoff participation is not recorded.',
  'eligibility.reason.PLAYOFF_PARTICIPATION_BELOW_MINIMUM':
    '{appearances} appearances in {matches} matches is below {minimumPercent}%.',
  'eligibility.reason.ROSTER_RULES_NOT_DEFINED': 'The rules define no roster sizes for this team.',
  'eligibility.reason.ROSTER_SKATERS_MINIMUM_DEPENDS_ON_REVIEW':
    'The minimum of {minimum} skaters is reached only if the athletes under review are confirmed.',
  'eligibility.reason.ROSTER_SKATERS_MINIMUM_NEEDS_PROMOTED_COUNT_REVIEW':
    'The minimum of {minimum} skaters is reached only by counting promoted athletes.',
  'eligibility.reason.ROSTER_SKATERS_BELOW_MINIMUM':
    '{counted} eligible skaters; at least {minimum} are needed.',
  'eligibility.reason.ROSTER_SKATERS_ABOVE_MAXIMUM':
    '{counted} skaters; at most {maximum} are allowed.',
  'eligibility.reason.ROSTER_GOALKEEPERS_MINIMUM_DEPENDS_ON_REVIEW':
    'The minimum of {minimum} goalkeepers is reached only if the athletes under review are confirmed.',
  'eligibility.reason.ROSTER_GOALKEEPERS_MINIMUM_NEEDS_PROMOTED_COUNT_REVIEW':
    'The minimum of {minimum} goalkeepers is reached only by counting promoted athletes.',
  'eligibility.reason.ROSTER_GOALKEEPERS_BELOW_MINIMUM':
    '{counted} eligible goalkeepers; at least {minimum} are needed.',
  'eligibility.reason.ROSTER_GOALKEEPERS_ABOVE_MAXIMUM':
    '{counted} goalkeepers; at most {maximum} are allowed.',
  'eligibility.reason.ROSTER_PROMOTION_QUOTA_EXCEEDED':
    '{counted} temporarily promoted athletes; at most {maximum} are allowed.',
  'eligibility.reason.ROSTER_HAS_INELIGIBLE_ENTRIES': '{count} athletes are not eligible.',
  'eligibility.reason.ROSTER_HAS_ENTRIES_UNDER_REVIEW': '{count} athletes need review.',
  'error.roster.athleteNotActive': 'Only an active athlete can be selected for a roster.',
  'error.roster.shirtNumberTaken': 'Another athlete of this roster wears this shirt number.',
  'error.roster.finalized': 'This roster is finalized. Reopen it to change it.',
  'error.roster.empty': 'Select at least one athlete before finalizing the roster.',
  'error.roster.notFinalizable':
    'The roster cannot be finalized: an athlete is not eligible or the roster breaks a rule. Check eligibility to see why.',
  'error.roster.acknowledgementRequired':
    'Something on this roster still needs review. State in words that you reviewed it, then finalize.',
  'error.roster.notFinalized': 'This roster is not finalized.',
} as const;

export type TranslationKey = keyof typeof en;
